import * as API from "../services/api_services";
import { fetchEntityConfig } from "../services/api_services";
import mqtt from "mqtt";
import Timeout from "./timeout";
import { TEXTS, DEFAULT_RESULT_CONFIG } from "./constants";
import { BACK_INDICATORS, EXCLUDE_INDICATORS } from "../../constants";

import { getEnvironmentFromDomain, getApiBaseUrl } from "../../utils/helperFunctions";
import { v4 as uuidv4 } from "uuid";
import * as UAParser from "ua-parser-js";

const WS_PROD_URL = 'wss://api.enncrypto.com/ws-upload';
const WS_STAGE_URL = 'wss://s.api.enncrypto.com/ws-upload';
const WS_QA_URL = 'wss://q.api.enncrypto.com/ws-upload';
const WS_DEFAULT_URL = 'wss://d.api.enncrypto.com/ws-upload';

const MQTT_PROD_CONFIG = {
  url: "wss://emqxlistener.ops.enncrypto.com/mqtt",
  username: "admin",
  password: "z2gP5Y1QXhO9",
};
const MQTT_QA_CONFIG = {
  url: "wss://dev-emqxlistener.ops.enncrypto.com/mqtt",
  username: "admin",
  password: "8hEg1F3q3KuP1qy",
};

// Default result mapping for aggregation logic
const DEFAULT_RESULT_MAPPING = {
  GENUINE: 'GENUINE',
  CANNOT_CONFIRM: 'CANNOT_CONFIRM', 
  PHOTO_QUALITY_ISSUE: 'PHOTO_QUALITY_ISSUE',
  CONNECTION_TIMED_OUT: 'CONNECTION_TIMED_OUT',
  POTENTIAL_FAKE: 'POTENTIAL_FAKE',
};

// Default threshold values for result aggregation
const DEFAULT_GENUINE_THRESHOLD = 1;
const DEFAULT_FAKE_THRESHOLD = 1;
const DEFAULT_CANNOT_CONFIRM_THRESHOLD = 1;
const DEFAULT_PHOTO_QUALITY_ISSUE_THRESHOLD = 1;

function getWSURL(): string {
  const env = getEnvironmentFromDomain();
  switch (env) {
    case 'prod':
      return WS_PROD_URL;
    case 'stage':
      return WS_STAGE_URL;
    case 'qa':
      return WS_QA_URL;
    default:
      return WS_DEFAULT_URL;
  }
}

function getMqttCredentials(): MqttCredentials {
  const env = getEnvironmentFromDomain();
  switch (env) {
    case 'prod':
    case 'stage':
      return MQTT_PROD_CONFIG;
    default:
      return MQTT_QA_CONFIG;
  }
}

export interface UserLocation {
  lat: number | null;
  long: number | null;
}

export interface State {
  loaded: boolean;
  cameraPermission: boolean;
  locationPermission: boolean;
  showViewfinder: boolean;
  scanningQR: boolean;
  qrInCenter: boolean;
  qrMatched: boolean;
  verifying: boolean;
  resultsShown: boolean;
  uploadingImages: boolean;
  error: Error | null;
}

export interface DeviceMetadata {
  camList: any;
  devicePixelRatio?: number;
  enableTestDataCollection?: any;
}

export interface SplashConfig {
  logo?: string;
  text?: string;
  backgroundImage?: string;
  showStartButton?: string;
}

export interface ViewFinderConfig {
  preScanText?: string;
  focusText?: string;
  invalidQRText?: string;
  verifyingText?: string;
  capturingText?: string;
  moveCloserText?: string;
  moveAwayText?: string;
  stabilizeText?: string;
  perfectPositionText?: string;
  improveQualityText?: string;
}

export interface ResultConfig {
  button2Icon: string;
  button1Icon: any;
  scanAgainIcon: any;
  textBgColour: string;
  statusColour: string;
  bgColour: string;
  icon: string;
  text: string;
  status: string;
  scanAgainButton: string | null;
  button1: string | null;
  button1Link?: string;
  button2: string | null;
  button2Link?: string;
  background: string;
  color: string;
  showThisResult: boolean;
  homeButton?: string | null;
  homeButtonIcon?: string | null;
  supportButton?: string | null;
  supportIcon?: string | null;
}

export interface AutoCaptureConfig {
  scanRegionPercentage: number;
  maxScansPerSecond: number;
  enabled: boolean;
  qrPhysicalSize: {
    minWidth: number;
    maxWidth: number;
    minWidthIOS: number;
    maxWidthIOS: number;
  };
  stabilityConfig: {
    qrNonDetectionBufferClearThreshold: number;
    qrNonDetectionBufferBreakThreshold: any;
    requiredStableFrames: number;
    stabilityThreshold: number;
  };
}

export interface ExperienceConfig {
  brand_engagement?: {
    customSplashScreenConfig?: SplashConfig;
    splashScreenTimeOut?: number;
    placeholder_question_mark_content?: string;
    howToScanHeader?: string;
    howToScanButton?: string;
    howToScanTimeOut?: number;
    isAutoCapture?: boolean;
    autoCaptureConfig?: AutoCaptureConfig;
  };
  viewFinderConfig?: ViewFinderConfig;
  ui_result_config?: Record<string, ResultConfig>;
  image_count?: number;
  qrUrl?: string;
  shortId?: string;
}

export interface Config {
  configs: {
    overlay_file_url: string;
    experience_config?: ExperienceConfig;
  };
}

export interface SuccessCallback {
  (data: { status: string }): void;
}

export interface ErrorCallback {
  (error: { type: string; message?: string; error?: any }): void;
}

export interface ToggleCallback {
  (): void;
}

export interface PosthogEvent {
  sendPostHogEvent: (
    eventName: string,
    properties: Record<string, any>
  ) => void;
}

export interface QRDetectionResult {
  data: string;
  cornerPoints: { x: number; y: number }[];
}

export interface QRDetectionBuffer {
  timestamp: number;
  qrData: string;
  cornerPoints: { x: number; y: number }[];
  physicalSize: { width: number; height: number };
  isValid: boolean; // true if within size range, false otherwise
}

export interface MqttCredentials {
  url: string;
  username: string;
  password: string;
}

export interface WSMessageHandlers {
  [key: string]: (data: any) => void;
}

export interface MQTTMessageHandlers {
  [key: string]: (data: any) => void;
}

export default class VyuScanAutoSDK {
   private static readonly COUNTDOWN_TIMER_STYLES = `
  .countdown-timer-overlay {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .countdown-timer-container {
    position: relative;
    width: 150px;
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .countdown-timer-digit {
    color: #FFF;
    text-align: center;
    font-family: "OpenSans", sans-serif;
    font-size: 64px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    z-index: 201;
}

  .countdown-progress-circle {
    position: absolute;
    top: 0;
    left: 0;
    width: 150px;
    height: 150px;
    transform: rotate(-90deg);
  }

  .countdown-progress-bar {
    fill: none;
    stroke: white;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-dasharray: 440;
    stroke-dashoffset: 440;
    transition: stroke-dashoffset 1s linear;
    filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.5));
  }
`;
  // DOM elements
  private videoElement: HTMLVideoElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null;
  private overlay: HTMLDivElement | null;
  private toast: HTMLDivElement | null;

  // State variables
  public state: State;

  // Configuration variables (from API)
  private config: any;
  private productId: string | null;
  private sessionId: string;
  private imageCount: number;
  private imageCountUploaded: number;
  private qrUrl: string;
  private shortId: string;
  private previewImage: string;
  private verifySessionStatus: string;
  private userLocation: UserLocation;
  private serialId?: string;
  private enableMockResults: boolean = false;
  private showSupportPopupCallback: ((config: any) => void) | null = null;
  private autoCaptureConfig: AutoCaptureConfig = {
    maxScansPerSecond: 10,
    scanRegionPercentage: 0.5,
    enabled: true,
    qrPhysicalSize: {
      minWidth: 135,
      maxWidth: 300,
      minWidthIOS: 135,
      maxWidthIOS: 250,
    },
    stabilityConfig: {
      requiredStableFrames: 5,
      stabilityThreshold: 30,
      qrNonDetectionBufferClearThreshold: 2800,
      qrNonDetectionBufferBreakThreshold: 1500,
    },
  };

  private qrDetectionBuffer: QRDetectionBuffer[] = [];
  private autoCaptureTimeout: number | null = null;
  private isAutoCaptureInProgress: boolean = false;

  // Timers and callbacks
  private timeoutInstance: Timeout | null;
  private qrScanner: any | null;
  private errorCallbackFn: ErrorCallback | null;
  private toggleCallback: ToggleCallback | null;
  private posthogEvent: PosthogEvent | null;
  private stream: MediaStream | null;
  private intervalId: number | undefined;

  // DOM elements cache
  private gridCells: HTMLDivElement[];
  private centerGridCell: HTMLDivElement | null;
  private mqttClient: mqtt.MqttClient | null = null;
  private mqttSessionExpireTopic: string = "";
  private readonly mqttMessageHandlers = new Map<string, (data: any) => void>();
  private messageHandler: any;
  private environment: string = "";
  private qrCropPaddingRatio: number = 1.5;
  private zoomLevel: number = 3.0;
  private scannerTimeoutDebounce: number = 0;
  private verificationTimeoutId: number | null = null;
  private verifyingTimerInterval: number | null = null;
  
  private wsClient: WebSocket | null = null;
  private wsURL: string = "";
  private readonly wsMessageHandlers = new Map<string, (data: any) => void>();
  
  // Result aggregation properties
  private fakeThreshold: number = DEFAULT_FAKE_THRESHOLD;
  private genuineThreshold: number = DEFAULT_GENUINE_THRESHOLD;
  private cannotConfirmThreshold: number = DEFAULT_CANNOT_CONFIRM_THRESHOLD;
  private photoQualityIssueThreshold: number = DEFAULT_PHOTO_QUALITY_ISSUE_THRESHOLD;
  private resultMapping = DEFAULT_RESULT_MAPPING;
  private mqttImageResultProcessed: boolean = false;
  private productScanId: string = "";
  private totalImageCount: number = 1;
  private currentBubbles: { x: number; y: number; element: HTMLDivElement }[] =
    [];
  private bubbleRemovalTimer: any = null;
  private productDetectedTimer: number | null = null;
  private countdownInterval: number | null = null;
  private sessionUuid: any
  private qrCenterStateDebounce: {
    lastState: boolean;
    stateChangeTime: number;
    stabilityThreshold: number;
    timeoutId: number | null;
    refreshIntervalId?: number;
    lastUiUpdateTime?: number;
  } = {
    lastState: false,
    stateChangeTime: 0,
    stabilityThreshold: 500,
    timeoutId: null,
    lastUiUpdateTime: 0,
  };
  selectedDeviceCameraId: string | null;
  productConfigId: any;
  ipLocation: any;
  lastQrDetectionEvent: { qrData: any; timestamp: number; isExpected: boolean };
  isDetectedOnce: any;
  private lastQrDetectionTime: number = 0;
  private stableBufferDebounceTimeout: number | null = null;
  private isFirstQrDetection: boolean = true;
  // Image resizing configuration
  private shouldResizeImage: boolean = true;
  // Missing properties for compatibility
  private successCallbackFn: SuccessCallback | null = null;
  private useWebSocketUpload: boolean = false;
  private productDetectedStartTime: number = 0;
  
  // Entity config properties
  private entityConfigPromise: Promise<any> | null = null;
  private isGeoFencingEnabled: boolean = false;
  private isSerialCodeEnabled: boolean = false;
  constructor(videoElement: HTMLVideoElement) {
    // DOM elements
    this.videoElement = videoElement;
    this.canvas = document.createElement("canvas");
    this.ctx = this.canvas.getContext("2d");
    this.overlay = null;
    this.toast = null;
    this.isDetectedOnce = false;
    this.state = {
      loaded: false,
      cameraPermission: false,
      locationPermission: false,
      showViewfinder: false,
      scanningQR: false,
      qrInCenter: false,
      qrMatched: false,
      verifying: false,
      resultsShown: false,
      uploadingImages: false,
      error: null,
    };
    this.config = null;
    this.productId = null;
    this.sessionId = "";
    this.imageCount = 1;
    this.totalImageCount = 1;
    this.imageCountUploaded = 0;

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const shortIdParam = urlParams.get("shortId");

      if (shortIdParam) {
        console.log(
          "[VyuScanAutoSDK] Found shortId in query params (raw):",
          shortIdParam
        );

        let cleanShortId = this.cleanUrlParameter(shortIdParam);

        if (cleanShortId && cleanShortId.length > 0) {
          console.log("[VyuScanAutoSDK] Cleaned shortId:", cleanShortId);

          this.shortId = cleanShortId;
          this.qrUrl = cleanShortId;
        } else {
          console.warn(
            "[VyuScanAutoSDK] ShortId parameter is empty after cleaning"
          );
          this.qrUrl = "";
          this.shortId = "";
        }
      } else {
        this.qrUrl = "";
        this.shortId = "";
      }
    } catch (error) {
      console.error("[VyuScanAutoSDK] Error parsing URL:", error);
    }

    this.previewImage = "";
    this.verifySessionStatus = "";
    this.userLocation = { lat: null, long: null };
    this.stream = null;
    this.timeoutInstance = null;
    this.qrScanner = null;
    this.successCallbackFn = null;
    this.errorCallbackFn = null;
    this.toggleCallback = null;
    this.posthogEvent = null;

    this.gridCells = [];
    this.centerGridCell = null;
    this.updateCanvasSize = this.updateCanvasSize.bind(this);
    this.handleScanAgain = this.handleScanAgain.bind(this);

    this.videoElement.addEventListener("loadeddata", this.updateCanvasSize);
    window.addEventListener("resize", this.updateCanvasSize);

    window.addEventListener("beforeunload", (event) => {
      this.cleanup();
      event.preventDefault();
    });
    window.addEventListener("unload", () => {
      this.cleanup();
    });
  }


  private cleanUrlParameter(param: string): string {
    if (!param) return "";

    let cleaned = decodeURIComponent(param).trim();

    if (
      (cleaned.startsWith('"') && cleaned.endsWith('"')) ||
      (cleaned.startsWith("'") && cleaned.endsWith("'"))
    ) {
      cleaned = cleaned.slice(1, -1);
    }

    cleaned = cleaned.replace(/^["']|["']$/g, "");

    return cleaned.trim();
  }
 
  public async init(
    toggleCallback: ToggleCallback | null,
    posthogEvent: PosthogEvent | null,
    successCallback: SuccessCallback | null,
    errorCallback: ErrorCallback | null,
    ipLocation?: any,
    productConfig?: any,
    showSupportPopup?: (config: any) => void,
  ): Promise<void> {
    try {
      console.log("Initializing VyuScanAutoSDK...");

      this.toggleCallback = toggleCallback;
      this.posthogEvent = posthogEvent;
      this.successCallbackFn = successCallback;
      this.errorCallbackFn = errorCallback;
      this.ipLocation = ipLocation;
      this.productId = this.getProductIdFromUrl();
      this.showSupportPopupCallback = showSupportPopup || null;
      const configResponse = productConfig;
      if (configResponse) {
        if (!configResponse || !configResponse.configs) {
          throw new Error("Invalid config response");
        }

        this.config = configResponse;
        this.imageCount =
          configResponse.configs.experience_config?.image_count || 1;
        this.totalImageCount = this.imageCount;
        this.productConfigId = configResponse?.configs.product_config_id;

        const brandEngagement =
          configResponse.configs.experience_config?.brand_engagement;


        if (brandEngagement?.autoCaptureConfig) {
          this.autoCaptureConfig = {
            ...this.autoCaptureConfig,
            ...brandEngagement.autoCaptureConfig,
          };
          console.log("Auto capture config loaded:", this.autoCaptureConfig);
        }

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

        if (isIOS && brandEngagement?.iosZoomValue) {
          this.zoomLevel = brandEngagement.iosZoomValue;
        } else if (!isIOS && brandEngagement?.androidZoomValue) {
          this.zoomLevel = brandEngagement.androidZoomValue;
        } else {
          this.zoomLevel = isIOS ? 3.0 : 2.5;
        }
        
        // Extract entity config flags from app config or brand engagement
        this.isGeoFencingEnabled = brandEngagement?.isGeoFencingEnabled ?? false;
        this.isSerialCodeEnabled = brandEngagement?.isSerialCodeEnabled ?? true;
        
        console.log("Config loaded successfully:", this.config);
        console.log("Entity config flags:", {
          isGeoFencingEnabled: this.isGeoFencingEnabled,
          isSerialCodeEnabled: this.isSerialCodeEnabled
        });
      } else {
        throw new Error("Product configuration not provided");
      }

      this.state.loaded = true;

      if (successCallback) {
        successCallback({ status: "CONFIG_LOADED" });
      }
    } catch (error) {
      console.error("Error initializing VyuScanAutoSDK:", error);

      if (errorCallback) {
        errorCallback({ type: "INIT_ERROR", error });
      }
    }
  }



  public async getLocationCoords(): Promise<UserLocation> {
    try {
      this.showLoader();
      console.log("[VYU-WRAPPER] Starting location coordinates request");
      const STORAGE_KEY = "user_location_data";
      const EXPIRY_TIME = 30 * 60 * 1000;
      const storedData = localStorage.getItem(STORAGE_KEY);

      interface StoredLocationData extends UserLocation {
        timestamp: number;
      }

      if (storedData) {
        console.log(
          "[VYU-WRAPPER] Found stored location data, checking validity"
        );
        try {
          const parsedData = JSON.parse(storedData) as StoredLocationData;
          const currentTime = new Date().getTime();
          const dataAge = currentTime - parsedData.timestamp;
          const dataAgeMinutes = Math.floor(dataAge / 60000);

          console.log(
            `[VYU-WRAPPER] Stored data age: ${dataAgeMinutes} minutes (expires at ${
              EXPIRY_TIME / 60000
            } minutes)`
          );

          if (
            dataAge < EXPIRY_TIME &&
            parsedData.lat !== null &&
            parsedData.long !== null
          ) {
            console.log(
              `[VYU-WRAPPER] Using cached location: lat=${parsedData?.lat?.toFixed(
                4
              )}, long=${parsedData?.long?.toFixed(4)}`
            );
            this.userLocation = {
              lat: parsedData.lat,
              long: parsedData.long,
            };
            return this.userLocation;
          } else {
            console.log(
              "[VYU-WRAPPER] Cached data expired, fetching fresh coordinates"
            );
          }
        } catch (error) {
          console.error(
            "[VYU-WRAPPER] Error parsing stored location data:",
            error
          );
          console.log(
            "[VYU-WRAPPER] Will fetch fresh coordinates due to parse error"
          );
        }
      } else {
        console.log(
          "[VYU-WRAPPER] No cached location data found, requesting new coordinates"
        );
      }

      try {
        console.log(
          "[VYU-WRAPPER] Requesting geolocation permission from browser"
        );
        const startTime = performance.now();
        console.time("[VYU-WRAPPER] Location API call");

        const coordinates = await new Promise<GeolocationPosition>(
          (resolve, reject) => {
            navigator.geolocation?.getCurrentPosition(resolve, reject);
          }
        );

        const endTime = performance.now();
        console.timeEnd("[VYU-WRAPPER] Location API call");
        const timeInSeconds = ((endTime - startTime) / 1000).toFixed(2);
        const timeInMilliseconds = (endTime - startTime).toFixed(0);
        console.log(
          `[VYU-WRAPPER] Location API call took ${timeInSeconds}s (${timeInMilliseconds}ms)`
        );

        this.userLocation = {
          lat: coordinates.coords.latitude,
          long: coordinates.coords.longitude,
        };

        console.log(
          `[VYU-WRAPPER] New coordinates: lat=${this.userLocation?.lat.toFixed(
            4
          )}, long=${this.userLocation?.long.toFixed(4)}`
        );

        const dataToStore: StoredLocationData = {
          ...this.userLocation,
          timestamp: new Date().getTime(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
        console.log(
          "[VYU-WRAPPER] Stored new coordinates in localStorage with current timestamp"
        );

        return this.userLocation;
      } catch (error) {
        console.error("[VYU-WRAPPER] Error getting geolocation:", error);
        throw error;
      }
    } catch (error) {
    } finally {
      this.hideLoader();
    }
  }
  private async initializeWebSocket(): Promise<void> {

    await new Promise<void>((resolve, reject) => {
      // Cleanup existing connection
      if (this.wsClient !== null) {
        if (this.wsClient.readyState === WebSocket.OPEN) {
          this.wsClient.close();
        }
        this.wsClient = null;
      }

      this.wsURL = getWSURL();
      console.log('[WebSocket] Attempting to connect to:', this.wsURL);
      const socket = new WebSocket(this.wsURL);

      // Connection timeout
      const connectionTimeout = setTimeout(() => {
        socket.close();
        reject(new Error('WebSocket connection timeout - check your internet connection'));
      }, 10000);

      socket.onopen = () => {
        clearTimeout(connectionTimeout);
        this.wsClient = socket;
        console.log('[WebSocket] Connection established successfully');
        this.setupWSMessageRouter();

        // Setup handlers for active connection
        socket.onclose = (closeEvent) => {
          this.wsClient = null;
          console.warn('[WebSocket] Connection lost:', {
            code: closeEvent.code,
            reason: closeEvent.reason,
            wasClean: closeEvent.wasClean,
          });
        };

        socket.onerror = (event) => {
          console.error('[WebSocket] Error during active connection:', event);
        };

        resolve();
      };

      socket.onerror = (event) => {
        clearTimeout(connectionTimeout);
        console.error('[WebSocket] Connection error:', event);
        reject(new Error('WebSocket connection failed'));
      };

      socket.onclose = (closeEvent) => {
        clearTimeout(connectionTimeout);
        if (this.wsClient === null) {
          console.error('[WebSocket] Connection closed during initialization:', closeEvent.code);
          reject(new Error(`WebSocket connection closed: ${closeEvent.code}`));
        }
      };
    });
  }

  private setupWSMessageRouter(): void {
    if (!this.wsClient) return;

    this.wsClient.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.debug('[WebSocket] Received message type:', data.type);
        
        const handler = this.wsMessageHandlers.get(data.type);
        if (typeof handler === 'function') {
          handler(data);
        } else {
          console.warn('[WebSocket] No handler registered for message type:', data.type);
          // Fallback to legacy handling
          this.handleDefaultWSMessage(data);
        }
      } catch (error) {
        console.error('[WebSocket] Failed to parse message:', error);
      }
    };
  }

  private async initializeMQTTAsync(sessionId: string): Promise<void> {
    console.log('[MQTT] Attempting to connect..., Session ID:', sessionId);
    
    await new Promise<void>((resolve, reject) => {
      this.environment = getEnvironmentFromDomain();
      const credentials = getMqttCredentials();
      
      const client = mqtt.connect(credentials.url, {
        username: credentials.username,
        password: credentials.password,
        clean: true,
        clientId: sessionId,
        rejectUnauthorized: false,
      });

      // Handle MQTT connection error
      client.on('error', (err) => {
        console.error('[MQTT] Connection Error:', err.message);
        reject(new Error('MQTT Connection Error: ' + err.message));
      });

      const connectionTimeout = setTimeout(() => {
        client.end();
        reject(new Error('MQTT connection timeout - check your internet connection'));
      }, 10000);

      client.on('connect', (conn) => {
        clearTimeout(connectionTimeout);
        console.log('[MQTT] Connection established successfully');
        this.mqttClient = client;

        // Set up event handlers
        this.mqttClient.on('error', (error) => {
          console.error('[MQTT] Error:', error);
        });

        this.mqttClient.on('disconnect', () => {
          console.log('[MQTT] Disconnected');
          this.mqttClient = null;
        });

        this.mqttClient.on('end', () => {
          console.log('[MQTT] Connection ended');
          this.mqttClient = null;
        });

        this.mqttClient.on('close', () => {
          console.log('[MQTT] Connection closed');
          this.mqttClient = null;
        });

        this.setupMQTTMessageRouter();
        
        resolve();
      });
    });
  }

  private registerWSMessageHandler(messageType: string, handler: (data: any) => void): void {
    this.wsMessageHandlers.set(messageType, handler);
    console.log(`Registered WebSocket handler for message type: ${messageType}`);
  }

  private handleDefaultWSMessage(data: any): void {
    // Handle common WebSocket messages that don't need specific handlers
    if (data.status === 'session_ready') {
      console.log('Session is ready for image upload via WebSocket');
    } else if (data.status === 'upload_complete') {
      console.log('Image upload completed via WebSocket:', data);
    } else if (data.status === 'error') {
      console.error('WebSocket error message:', data.message || data.error);
      // Fall back to HTTP upload on WebSocket errors
      this.useWebSocketUpload = false;
    } else {
      console.log('Unhandled WebSocket message:', data);
    }
  }

  private sendWSMessage(message: any): boolean {
    try {
      if (this.wsClient && this.wsClient.readyState === WebSocket.OPEN) {
        this.wsClient.send(JSON.stringify(message));
        console.log('WebSocket message sent:', message);
        return true;
      } else {
        console.log('WebSocket not connected, cannot send message');
        return false;
      }
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      return false;
    }
  }
    private async getMainCameraDeviceId(): Promise<string | null> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );

      if (videoDevices.length === 0) {
        return null;
      }
      let deviceId = "";

      for (const device of videoDevices) {
        const label = device.label.toLowerCase();

        const isBackCamera = BACK_INDICATORS.some((indicator) =>
          label.includes(indicator.toLowerCase())
        );

        const isExcluded = EXCLUDE_INDICATORS.some((indicator) =>
          label.includes(indicator.toLowerCase())
        );

        if (isBackCamera && !isExcluded) {
          console.log(
            `Found main camera: ${device.label} (${device.deviceId})`
          );
          deviceId = device.deviceId;
        }
      }
      if (deviceId) {
        return deviceId;
      }

      const firstCamera = videoDevices[0];
      console.log(
        `Using first available camera: ${firstCamera.label} (${firstCamera.deviceId})`
      );
      return firstCamera.deviceId;
    } catch (error) {
      console.error("Error getting main camera device ID:", error);
      return null;
    }
  }
  private async setWSSessionId(sessionId: string): Promise<void> {
    console.log('[WebSocket] Setting session ID:', sessionId);
    if (!this.wsClient) {
      throw new Error('[WebSocket] connection not established');
    }
    // if (!this.userLocation?.lat) {
    //   this.userLocation = {
    //     lat:
    //       this.ipLocation && this.ipLocation.latitude
    //         ? this.ipLocation.latitude
    //         : "",
    //     long:
    //       this.ipLocation && this.ipLocation.longitude
    //         ? this.ipLocation.longitude
    //         : "",
    //   };
    // }
    const mainCameraDeviceId = await this.getMainCameraDeviceId();
    //const camList = [];

      // try {
      //   const stream = await navigator.mediaDevices.getUserMedia({
      //     video: { deviceId: { ideal: mainCameraDeviceId } },
      //   });

      //   const track = stream?.getVideoTracks()[0];
      //   const capabilities = track?.getCapabilities();
      //   if (capabilities) camList.push(capabilities);
      //   track.stop();
      // } catch (err) {
      //   console.error(
      //     "Error getting capabilities for device:",
      //     mainCameraDeviceId,
      //     err
      //   );
      //   camList.push("unknown");
      // }

    // console.log("iplocation", this.ipLocation);
    if (!this.userLocation?.lat)
    this.userLocation = await this.getLocationCoords();
    const camList = mainCameraDeviceId
    ? [{ deviceId: mainCameraDeviceId }] 
    : ["unknown"];
    const storedDeviceModel = localStorage.getItem('deviceModel');
    const ua = new UAParser.UAParser();
    const deviceDetails = ua.getResult();
    const deviceModel = storedDeviceModel != null
      ? `${deviceDetails.device?.model} ${storedDeviceModel}`
      : deviceDetails.device?.model;

    const enhancedDeviceDetails = {
      ...deviceDetails,
      device: {
        ...deviceDetails.device,
        model: deviceModel,
      },
    };

    const metadata = {
      clientId: uuidv4(),
      sessionId,
      context: {
        user_agent: navigator.userAgent,
        phone: deviceModel,
        clientName: this.config?.configs?.clientName,
        browser: deviceDetails.browser?.toString(),
        session_metadata: {
          geo_address: {
            geo_lat: this.userLocation?.lat?.toString(),
            geo_long: this.userLocation?.long?.toString(),
          },
          device_details: enhancedDeviceDetails,
        },
        appType: deviceDetails.device?.type ?? 'desktop',
        app_id: 1,
        bandwidth: 5,
        imageTag: 'static',
        scanId: this.productScanId,
        createdBy: "vyu-scan-app",
        updatedBy: "vyu-scan-app",
        productConfigId: this.productConfigId,
        flowType: 'Essential',
        shortId: this.shortId,
        sessionType: [
          {
            sessionRequest: "QR",
            serviceName: "QR",
            additionalData: {
              deviceId: mainCameraDeviceId,
              productSku: this.productScanId,
            },
          },
        ],
         camList: JSON.stringify(camList),
      },
    };

    const responseSessionId = await new Promise<string>((resolve, reject) => {
      // Set up handler for session_init response
      this.registerWSMessageHandler('session_init', (data) => {
        const sessionId = data?.session_id;
        console.log('[WebSocket] Session initialized:', sessionId);
        
        // Cleanup handler
        this.wsMessageHandlers.delete('session_init');
        resolve(sessionId);
      });

      // Set timeout for session init
      const sessionTimeout = setTimeout(() => {
        this.wsMessageHandlers.delete('session_init');
        reject(new Error('WebSocket session initialization timeout'));
      }, 10000);

      console.log('[WebSocket] Sending session initialization...', sessionId);
      const success = this.sendWSMessage(metadata);
      if (!success) {
        clearTimeout(sessionTimeout);
        this.wsMessageHandlers.delete('session_init');
        reject(new Error('Failed to send session initialization message'));
      }
    });

    if (!responseSessionId || responseSessionId !== sessionId) {
      throw new Error('Invalid session ID response from WebSocket');
    }

    // Store the confirmed session ID (should match the UUID we sent)
    this.sessionId = responseSessionId;
    console.log('[WebSocket] Session ID confirmed:', this.sessionId);
  }

  private uploadImageViaWebSocket(dataUrl: string): Promise<any> {
    if (!this.wsClient || this.wsClient.readyState !== WebSocket.OPEN) {
      throw new Error('[WebSocket] Not connected');
    }

    // Extract base64 data (after comma)
    const base64Data = dataUrl.split(',')[1];
    
    const payload = {
      type: 'image_upload',
      scan_id: this.productScanId,
      context: {
       clientName: this.config?.configs?.clientName
      },
      image: base64Data,
    };

    console.log('[WebSocket] Sending image upload:', { 
      type: payload.type, 
      scan_id: payload.scan_id, 
      imageSize: base64Data.length 
    });
    
    const success = this.sendWSMessage(payload);
    
    if (!success) {
      throw new Error('[WebSocket] Failed to send image');
    }
    
    // For WebSocket uploads, we don't wait for response
    // The actual verification results come via MQTT
    return Promise.resolve({ status: 'uploaded' });
  }

  private async subscribeToImageResults(): Promise<void> {
    const topic = `${this.sessionId}-image-stream/results`;
    console.log('[MQTT] Subscribing to image results topic:', topic);

    if (!this.mqttClient) {
      throw new Error('[MQTT] client not connected');
    }

    let resultsReceived = 0;
    let acceptCount = 0;
    let fakeCount = 0;
    let photoQualityIssueCount = 0;
    let cannotConfirmCount = 0;

    await new Promise<void>((resolve, reject) => {
      this.mqttClient?.subscribe(topic, {}, (err) => {
        if (err) {
          reject(new Error(`Failed to subscribe to ${topic}: ${err.message}`));
          return;
        }

        console.log(`[MQTT] Subscribed to image results topic ${topic}`);

        this.mqttMessageHandlers.set(topic, (data) => {
          const detection = data;
          if (this.mqttImageResultProcessed) {
            console.debug(
              '[MQTT] Skipping because image result already processed. duplicate image result:',
              detection
            );
            return;
          }
          
          if (this.isValidDetection(detection)) {
            // Clear verification timeout since we got a valid detection
            if (this.verificationTimeoutId) {
              console.log('[MQTT] Clearing verification timeout due to valid detection');
              window.clearTimeout(this.verificationTimeoutId);
              this.verificationTimeoutId = null;
            }
            
            resultsReceived++;
            const prediction = detection.prediction_result;

            // Update counts based on prediction result
            // 0 = Genuine, 1 = Fake, -1 = Cannot confirm, -2 = Photo quality issue
            if (prediction === 0) acceptCount++;
            else if (prediction === 1) fakeCount++;
            else if (prediction === -2) photoQualityIssueCount++;
            else if (prediction === -1) cannotConfirmCount++;

            console.debug(
              `[MQTT] Result ${resultsReceived}/${this.totalImageCount}:`,
              prediction
            );

            const shouldDecide = this.shouldMakeDecision(
              acceptCount,
              fakeCount,
              photoQualityIssueCount,
              cannotConfirmCount,
              resultsReceived
            );

            // Process final result if we should decide or all results received
            if (shouldDecide || resultsReceived >= this.totalImageCount) {
              console.debug(
                `[MQTT] Final counts - Accept: ${acceptCount}, Fake: ${fakeCount}, Cannot: ${cannotConfirmCount}`
              );
              this.processFinalResult(
                acceptCount,
                fakeCount,
                photoQualityIssueCount,
                resultsReceived
              ).catch((error) => {
                console.error('[MQTT] Error processing final result:', error);
              });
            }

            // If we reach here, we continue waiting for more results
          } else {
            console.error(
              '[MQTT] Invalid detection result received:',
              detection
            );
            
            // Start verification timeout immediately for invalid detection
            if (!this.verificationTimeoutId) {
              console.log('[MQTT] Starting verification timeout due to invalid detection');
              this.verifyProduct(10000);
            }
          }
        });

        resolve();
      });
    });
  }

  private shouldMakeDecision(
    acceptCount: number,
    fakeCount: number,
    photoQualityIssueCount: number,
    cannotConfirmCount: number,
    totalReceived: number
  ): boolean {
    // Decide immediately if we have enough genuine results
    if (acceptCount > 0 && acceptCount >= this.genuineThreshold) {
      console.log('[MQTT] Genuine result received - proceeding immediately');
      return true;
    }

    // Decide if we've exceeded fake threshold
    if (fakeCount > 0 && fakeCount >= this.fakeThreshold) {
      console.log('[MQTT] Maximum fake threshold exceeded - declaring FAKE');
      return true;
    }

    // Decide if we've exceeded photo quality issue threshold
    if (
      this.photoQualityIssueThreshold > 0 &&
      photoQualityIssueCount >= this.photoQualityIssueThreshold
    ) {
      console.log(
        '[MQTT] Photo quality issue threshold exceeded - declaring PHOTO_QUALITY_ISSUE'
      );
      return true;
    }

    if (
      this.cannotConfirmThreshold > 0 &&
      cannotConfirmCount >= this.cannotConfirmThreshold
    ) {
      console.log(
        '[MQTT] Cannot confirm threshold exceeded - declaring CANNOT_CONFIRM'
      );
      return true;
    }

    if (totalReceived >= this.totalImageCount) {
      console.log('[MQTT] All images received - proceeding immediately');
      return true;
    }

    return false;
  }

  private async processFinalResult(
    acceptCount: number,
    fakeCount: number,
    photoQualityIssueCount: number,
    totalReceived: number
  ): Promise<void> {
    console.log(`[MQTT] Processing final result from ${totalReceived} images`);

    let sessionStatus = this.resultMapping.CANNOT_CONFIRM;

    if (acceptCount >= this.genuineThreshold) {
      // Any genuine = GENUINE
      sessionStatus = this.resultMapping.GENUINE;
    } else if (fakeCount >= this.fakeThreshold) {
      // Fake threshold met = FAKE
      sessionStatus = this.resultMapping.POTENTIAL_FAKE;
    } else if (
      this.photoQualityIssueThreshold > 0 &&
      photoQualityIssueCount >= this.photoQualityIssueThreshold
    ) {
      // Photo quality issue threshold met = PHOTO_QUALITY_ISSUE
      sessionStatus = this.resultMapping.PHOTO_QUALITY_ISSUE;
    } else {
      // All images received, no genuine, fake threshold not met = CANNOT_CONFIRM
      sessionStatus = this.resultMapping.CANNOT_CONFIRM;
    }

    console.log('[MQTT] Final verification status:', sessionStatus);

    const analysisResult = this.calculateAnalysisResult(
      acceptCount,
      fakeCount,
      photoQualityIssueCount
    );

    try {
      this.mqttImageResultProcessed = true;
      

      
      this.verifySessionStatus = sessionStatus;
      
      if (this.stream) {
        this.stream.getTracks().forEach((track) => {
          console.log(`Stopping camera track: ${track.kind}`);
          track.stop();
        });
        this.stream = null;
      }
      
      if (this.verificationTimeoutId) {
        window.clearTimeout(this.verificationTimeoutId);
        this.verificationTimeoutId = null;
      }
      

      const isGenuine = sessionStatus?.toLowerCase() === 'genuine';
      let isValidLocation = true
      if (this.entityConfigPromise) {
        console.log('Waiting for entity-config API call to complete...');
        try {
          const entityConfigResult = await this.entityConfigPromise;
          const serialCode = entityConfigResult?.starlinkResponse?.serialCode
          isValidLocation = entityConfigResult?.geoFenceResponse?.valid ?? true
          if (serialCode && isGenuine && this.shortId) {
            this.serialId = serialCode
            console.log('SerialCode from entity-config stored for GENUINE result:', this.serialId);
          } else {
            console.log('No serialCode in entity-config response');
            this.serialId = undefined;
          }
        } catch (error) {
          console.error('Entity-config API call failed, proceeding without serialCode:', error);
          this.serialId = undefined;
        }
      } else {
        this.serialId = undefined;
        if (!isGenuine) {
          console.log(' Not a GENUINE result - serialCode will not be displayed');
        }
      }
      
      // Determine the final UI result status based on region validation
      let finalUiStatus = sessionStatus;
      const regionExcluded = !isValidLocation;
      if (regionExcluded) {
        finalUiStatus = isGenuine ? 'REGION_EXCLUDED_GENUINE' : 'REGION_EXCLUDED_NON_GENUINE';
      }
      
      // Send session result with the updated UI status
      this.sendSessionResult(
        this.getPredictionLabel(analysisResult),
        analysisResult,
        finalUiStatus,
        this.genuineThreshold,
        this.fakeThreshold,
        this.sessionId
      );
      
      // Create result object for UI display
      const resultObject = {
        finalResult: sessionStatus,
        sessionUuid: this.sessionUuid,
        serialCode: this.serialId,
        regionExcluded: regionExcluded,
        status: 'COMPLETED'
      };
      
      console.log('[MQTT] Processing UI result with:', resultObject);
      
      // Display UI result
      await this.processScanResult(resultObject);

    } catch (error) {
      console.error('[MQTT] Error sending session result:', error);
      
      // Create error result object
      const errorResult = {
        status: 'ERROR',
        error: error.message || 'Unknown error',
        finalResult: 'ERROR'
      };
      
      // Use existing processScanResult for error handling too
      await this.processScanResult(errorResult);
    } finally {
      this.cleanUpAfterSessionResult();
    }
  }

  private calculateAnalysisResult(
    acceptCount: number,
    rejectCount: number,
    photoQualityIssueCount: number
  ): number {
    if (acceptCount >= this.genuineThreshold) return 0;
    if (rejectCount >= this.fakeThreshold) return 1;
    if (
      this.photoQualityIssueThreshold > 0 &&
      photoQualityIssueCount >= this.photoQualityIssueThreshold
    )
      return 4;
    return 2;
  }

  private getPredictionLabel(predictionResult: number): string {
    switch (predictionResult) {
      case 0:
        return 'GENUINE';
      case 1:
        return 'FAKE';
      case 2:
        return 'CANNOT_CONFIRM';
      case 4:
        return 'PHOTO_QUALITY_ISSUE';
      default:
        return 'UNKNOWN';
    }
  }

  private isValidDetection(data: any): boolean {
    return (
      data?.prediction_result !== null &&
      typeof data.prediction_result !== 'undefined' &&
      [0, 1, -1, -2].includes(data.prediction_result)
    );
  }

  private cleanUpAfterSessionResult(): void {
    console.log('[MQTT/WebSocket] Cleaning up after session result');
    
    // Clean up MQTT
    if (this.mqttClient) {
      this.mqttClient.end();
      this.mqttClient = null;
    }
    
    // Clean up WebSocket
    if (this.wsClient && this.wsClient.readyState !== WebSocket.CLOSED) {
      this.wsClient.close(1000, 'Session ended');
      this.wsClient = null;
    }
    
    this.mqttImageResultProcessed = false;
  }

  private async sendSessionResult(
    result: string,
    analysisResult: number,
    uiResult: string,
    genuineThreshold: number,
    fakeThreshold: number,
    sessionId: string
  ): Promise<void> {
    console.log('Ui result ->>>>>>', this.getResultConfig(uiResult).status || uiResult)
    const captureType = 'static'
    try {
      await API.sendSessionResult(
        result,
        analysisResult,
        this.getResultConfig(uiResult).status || uiResult,
        genuineThreshold,
        fakeThreshold,
        sessionId,
        captureType
      );
    } catch (error) {
      console.error('[API] Error sending session result:', error);
      throw error;
    }
  }


  private async processScanResult(result: any): Promise<void> {
    console.log("Processing scan result:", result);
    if(result?.sessionUuid) {
      this.sessionUuid = result.sessionUuid;
    }
    if (this.state.verifying) {
      if (result.finalResult) {
        if (this.verificationTimeoutId) {
          window.clearTimeout(this.verificationTimeoutId);
          this.verificationTimeoutId = null;
        }
        this.verifySessionStatus = result.finalResult;
        const regionExcluded = result?.regionExcluded;

        this.state.verifying = false;
        this.removeToast();
        this.showResult(this.verifySessionStatus, regionExcluded);
        if (this.posthogEvent) {
          this.posthogEvent.sendPostHogEvent("authentication_result_received", {
            result_status: this.verifySessionStatus,
            qr_id_scanned_initially: this.qrUrl || this.shortId,
            timestamp: new Date().toISOString(),
          });
        }
        return;
      }

      if (result.status === "IN_PROGRESS") {
        console.log("Verification in progress via MQTT");
        return;
      }

      if (result.status === "ERROR" || result.error) {
        console.error(
          "Verification error via MQTT:",
          result.error || "Unknown error"
        );
        this.state.verifying = false;
        this.removeToast();
        if (this.posthogEvent) {
          this.posthogEvent.sendPostHogEvent("authentication_result_received", {
            result_status: "ERROR",
            qr_id_scanned_initially: this.qrUrl || this.shortId,
            error_message_from_server: result.error || "Unknown error",
            timestamp: new Date().toISOString(),
          });
        }
        this.showResult("ERROR");
        return;
      }
    }
  }

  private getProductIdFromUrl(): string {
  console.log("[VyuScanAutoSDK] Getting product ID from URL path");
  
  const pathname = window.location.pathname;
  const productPathMatch = pathname.match(/\/product\/([^/?]+)/);
  
  if (productPathMatch && productPathMatch[1]) {
    const productId = productPathMatch[1];
    console.log("[VyuScanAutoSDK] Found product ID in path:", productId);
    return productId;
  }
  
  const urlParams = new URLSearchParams(window.location.search);
  const queryProductId = urlParams.get("productId");
  if (queryProductId) {
    console.log("[VyuScanAutoSDK] Found product ID in query params:", queryProductId);
    return queryProductId;
  }
  
  console.warn("[VyuScanAutoSDK] No product ID found in URL");
  return "";
}
  private createPulsatingBubbles(): void {
    // Create CSS styles for animations
    const style = document.createElement("style");
    if (!document.head.querySelector("#bubble-styles")) {
      style.id = "bubble-styles";
      style.innerHTML = `
      @keyframes pulse {
        0%, 100% { 
          transform: translate(-50%, -50%) scale(1); 
          opacity: 1; 
        }
        50% { 
          transform: translate(-50%, -50%) scale(1.4); 
          opacity: 0.8; 
        }
      }
      @keyframes ripple {
        0% { 
          transform: translate(-50%, -50%) scale(1); 
          opacity: 1; 
        }
        100% { 
          transform: translate(-50%, -50%) scale(2.5); 
          opacity: 0; 
        }
      }
    `;
      document.head.appendChild(style);
    }

    // Remove existing bubbles
    this.currentBubbles.forEach((b) => b.element.remove());
    this.currentBubbles = [];

    // Use larger centered area for bubbles (like in the design)
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // Larger centered area - 80% of screen width and 70% of screen height
    const width = screenWidth * 0.8;
    const height = screenHeight * 0.5;
    const x = (screenWidth - width) / 2;
    const y = (screenHeight - height) / 2;

    const bubbleCount = 25; // More bubbles to fill the larger area
    const minDistance = 35; // Reduce minimum distance for more bubbles

    for (let i = 0; i < bubbleCount; i++) {
      let bubbleX: number, bubbleY: number, overlapping: boolean;
      let attempts = 0;
      const maxAttempts = 50;

      // Find non-overlapping position
      do {
        bubbleX = x + Math.random() * width;
        bubbleY = y + Math.random() * height;
        overlapping = this.currentBubbles.some(
          (b) => Math.hypot(b.x - bubbleX, b.y - bubbleY) < minDistance
        );
        attempts++;
      } while (overlapping && attempts < maxAttempts);

      // Create bubble container
      const bubbleContainer = document.createElement("div");
      bubbleContainer.style.position = "fixed";
      bubbleContainer.style.left = `${bubbleX}px`;
      bubbleContainer.style.top = `${bubbleY}px`;
      bubbleContainer.style.width = "16px";
      bubbleContainer.style.height = "16px";
      bubbleContainer.style.zIndex = "105";
      bubbleContainer.style.pointerEvents = "none";

      // Vary bubble sizes slightly
      const bubbleSize = Math.random() > 0.5 ? "4px" : "6px";

      // Create inner bubble
      const innerBubble = document.createElement("div");
      innerBubble.style.width = bubbleSize;
      innerBubble.style.height = bubbleSize;
      innerBubble.style.background = "white";
      innerBubble.style.borderRadius = "50%";
      innerBubble.style.position = "absolute";
      innerBubble.style.top = "50%";
      innerBubble.style.left = "50%";
      innerBubble.style.transform = "translate(-50%, -50%) scale(1)"; // Start at scale(1)
      innerBubble.style.transformOrigin = "center center"; // Ensure scaling from center
      innerBubble.style.animation = "pulse 1.5s infinite ease-in-out";
      innerBubble.style.animationDelay = `${Math.random() * 0.5}s`;

      // Create ripple effect
      const ripple = document.createElement("div");
      ripple.style.width = bubbleSize;
      ripple.style.height = bubbleSize;
      ripple.style.border = "2px solid white";
      ripple.style.borderRadius = "50%";
      ripple.style.position = "absolute";
      ripple.style.top = "50%";
      ripple.style.left = "50%";
      ripple.style.transform = "translate(-50%, -50%) scale(1)"; // Start at scale(1)
      ripple.style.transformOrigin = "center center"; // Ensure scaling from center
      ripple.style.animation = "ripple 1.5s infinite ease-out";
      ripple.style.animationDelay = `${Math.random() * 0.5}s`;

      bubbleContainer.appendChild(innerBubble);
      bubbleContainer.appendChild(ripple);
      document.body.appendChild(bubbleContainer);

      this.currentBubbles.push({
        x: bubbleX,
        y: bubbleY,
        element: bubbleContainer,
      });
    }
  }

  private clearBubbles(): void {
    this.currentBubbles?.forEach((b) => b.element.remove());
    this.currentBubbles = [];
  }

  private showLoader(): void {
    if (document.getElementById("vyu-loader")) {
      return;
    }

    const loaderElement = document.createElement("div");
    loaderElement.id = "vyu-loader";
    loaderElement.className = "vyu-loader";
    loaderElement.style.position = "fixed";
    loaderElement.style.top = "0";
    loaderElement.style.left = "0";
    loaderElement.style.width = "100vw";
    loaderElement.style.height = "100vh";
    loaderElement.style.backgroundColor = "white";
    loaderElement.style.display = "flex";
    loaderElement.style.flexDirection = "column";
    loaderElement.style.justifyContent = "center";
    loaderElement.style.alignItems = "center";
    loaderElement.style.zIndex = "2005";

    const startLoadingAnimationContainer = document.createElement("div");
    startLoadingAnimationContainer.className = "vyu-loader__start";
    startLoadingAnimationContainer.style.display = "flex";
    startLoadingAnimationContainer.style.flexDirection = "column";
    startLoadingAnimationContainer.style.alignItems = "center";
    startLoadingAnimationContainer.style.justifyContent = "center";

    const startLoadingAnimation = document.createElement("img");
    startLoadingAnimation.src =
      "https://ennoventure-public-assets.s3.us-east-1.amazonaws.com/static/Enno_Loader.webp";
    startLoadingAnimation.style.width = "200px";
    startLoadingAnimation.style.height = "200px";
    startLoadingAnimation.style.marginBottom = "15px";

    const startLoadingTextElem = document.createElement("span");
    startLoadingTextElem.style.color = "#ffffff";
    startLoadingTextElem.style.fontFamily = "sans-serif";
    startLoadingTextElem.style.fontSize = "16px";
    startLoadingTextElem.style.textAlign = "center";
    startLoadingTextElem.style.maxWidth = "80%";
    startLoadingTextElem.style.position = "absolute";
    startLoadingTextElem.style.top = "80%";

    const loadingTexts = this.config?.configs?.experience_config
      ?.brand_engagement?.startLoadingTexts || [
      "Loading...",
      "Almost Done...",
      "Unlocking the truth behind your product...",
      "We are using cutting-edge tech to ensure authenticity...",
      "Connecting to authentication server...",
    ];

    startLoadingTextElem.textContent = loadingTexts[0];

    let count = 0;
    this.intervalId = window.setInterval(() => {
      if (count < loadingTexts.length) {
        startLoadingTextElem.textContent = loadingTexts[count];
        count++;
      } else {
        count = 0;
      }
    }, 1500);

    const styleElement = document.createElement("style");
    styleElement.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
    document.head.appendChild(styleElement);

    startLoadingAnimationContainer.appendChild(startLoadingAnimation);
    startLoadingAnimationContainer.appendChild(startLoadingTextElem);
    loaderElement.appendChild(startLoadingAnimationContainer);
    document.body.appendChild(loaderElement);
  }

  private hideLoader(): void {
    const loaderElement = document.getElementById("vyu-loader");
    if (loaderElement) {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = undefined;
      }
      loaderElement.style.display = "none";
      loaderElement.remove();
    }
  }

  private setupMQTTMessageRouter(): void {
    if (this.mqttClient == null) return;

    this.mqttClient.on('message', (topic, message) => {
      try {
        const data = JSON.parse(message.toString());
        console.info('[MQTT] Received message on topic:', topic);

        // Route by topic
        const handler = this.mqttMessageHandlers.get(topic);

        if (typeof handler === 'function') {
          handler(data);
        } else {
          console.warn('[MQTT] No handler for topic:', topic);
        }
      } catch (error) {
        console.error('[MQTT] Message parse error:', error);
      }
    });
  }

  public async startSession(): Promise<string> {
    try {
      this.showLoader();


      this.productScanId = this.productId || this.getProductIdFromUrl() || '';
      
      const experienceConfig = this.config?.configs?.experience_config;
      if (experienceConfig?.result_aggregation_config) {
        const aggregationConfig = experienceConfig.result_aggregation_config;
        this.fakeThreshold = aggregationConfig.fake_threshold ?? DEFAULT_FAKE_THRESHOLD;
        this.genuineThreshold = aggregationConfig.genuine_threshold ?? DEFAULT_GENUINE_THRESHOLD;
        this.cannotConfirmThreshold = aggregationConfig.cannot_confirm_threshold ?? DEFAULT_CANNOT_CONFIRM_THRESHOLD;
        this.photoQualityIssueThreshold = aggregationConfig.photo_quality_issue_threshold ?? DEFAULT_PHOTO_QUALITY_ISSUE_THRESHOLD;
      }
      
      if (experienceConfig?.image_count) {
        this.totalImageCount = experienceConfig.image_count;
        this.imageCount = experienceConfig.image_count;
      }

      // Result mapping from experience config
      if (experienceConfig?.result_aggregation_mapping) {
        this.resultMapping = {
          ...this.resultMapping,
          ...experienceConfig.result_aggregation_mapping,
        };
      }
      
      console.log('[SDK] Configured thresholds:', {
        genuine: this.genuineThreshold,
        fake: this.fakeThreshold,
        cannotConfirm: this.cannotConfirmThreshold,
        photoQuality: this.photoQualityIssueThreshold,
        totalImages: this.totalImageCount
      });

      const sessionId = uuidv4();
      this.sessionId = sessionId;
      console.log('[SDK] Generated UUID session ID for v2 flow:', sessionId);
      
      const urlParams = new URLSearchParams(window.location.search);
      const showSessionId = urlParams.get('showSessionId');
      if (showSessionId === 'true') {
        setTimeout(() => {
          this.createSessionIdDisplay();
        }, 2000);
      }
      
      const sessionTasks: Array<Promise<void>> = [];
      
      // Initialize WebSocket connection
      const wsTask = async (): Promise<void> => {
        await this.initializeWebSocket();
        await this.setWSSessionId(sessionId);
      };
      
      // Initialize MQTT connection 
      sessionTasks.push(wsTask(), this.initializeMQTTAsync(sessionId));
      
      // Start entity-config API call if needed (don't wait for response)
      if (this.isGeoFencingEnabled || this.isSerialCodeEnabled) {
        console.log('[SDK] Starting entity-config API call');
        this.entityConfigPromise = this.startEntityConfigCall();
      }
      
      // Execute WebSocket and MQTT initialization in parallel
      await Promise.all(sessionTasks);
      console.log('[SDK] WebSocket and MQTT initialization completed');
      
      await this.subscribeToImageResults();
      console.log('[SDK] Subscribed to image results');
      
      return sessionId;
    } catch (error) {
      console.error("Error starting session:", error);
      
      if (this.errorCallbackFn) {
        this.errorCallbackFn({
          type: "API_ERROR",
          message: "Failed to start session",
          error,
        });
      }
      
      // Cleanup on error
      if (this.wsClient) {
        this.wsClient.close();
        this.wsClient = null;
      }
      if (this.mqttClient) {
        this.mqttClient.end();
        this.mqttClient = null;
      }

      throw error;
    } finally {
      this.hideLoader();
    }
  }

  private async startEntityConfigCall(): Promise<any> {
    try {
      console.log('[SDK] Calling entity-config API');
      
      if (!this.shortId) {
        console.warn('[SDK] No shortId available for entity-config call');
        return null;
      }
      
      // Use same location logic as setWSSessionId method
      if (!this.userLocation?.lat) {
        this.userLocation = {
          lat:
            this.ipLocation && this.ipLocation.latitude
              ? this.ipLocation.latitude
              : "",
          long:
            this.ipLocation && this.ipLocation.longitude
              ? this.ipLocation.longitude
              : "",
        };
      }
      
      // Get fresh location coords if not available from IP location
      if (!this.ipLocation?.latitude && !this.userLocation?.lat) {
        this.userLocation = await this.getLocationCoords();
      }
      
      const entityConfigData = await fetchEntityConfig(
        this.shortId,
        this.userLocation.lat || undefined,
        this.userLocation.long || undefined,
        this.isGeoFencingEnabled,
        this.isSerialCodeEnabled
      );
      
      if (entityConfigData) {
        this.entityConfigData = entityConfigData;
        console.log('[SDK] Entity config data received:', entityConfigData);
        
        // Store serialCode if available
        if (entityConfigData.starlinkResponse?.serialCode) {
          console.log('[SDK] Serial code from entity-config:', entityConfigData.serialCode);
        }
      } else {
        console.warn('[SDK] No entity config data received');
      }
      
      return entityConfigData;
    } catch (error) {
      console.error('[SDK] Error calling entity-config API:', error);
      this.entityConfigData = null;
      return null;
    }
  }

  public async startViewfinder(): Promise<void> {
    console.log("Starting viewfinder");
    try {
      this.state.showViewfinder = true;
      if (this.posthogEvent) {
        this.posthogEvent.sendPostHogEvent("viewfinder_open", {
          initial_qr_id_expected: this.qrUrl || this.shortId,
          capture_mode: "auto",
          timestamp: new Date().toISOString(),
        });
      }

      await new Promise((resolve) => setTimeout(resolve, 100));

      //this.drawGrid();

      const viewFinderConfig =
        this.config?.configs?.experience_config?.viewFinderConfig || {};
      const preScanText = viewFinderConfig.preScanText || TEXTS.SCAN_QR;

      this.createToast(preScanText, "#242424", "#FFFFFF", "");

      try {
        await this.startCamera();
        console.log("Camera started successfully for viewfinder");
      } catch (cameraError) {
        console.error("Error starting camera in viewfinder:", cameraError);
        console.log("Retrying camera start after delay...");
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await this.startCamera();
      }

      try {
        if (this.qrScanner) {
          try {
            console.log("Cleaning up previous QR scanner");
            this.qrScanner.stop();
            this.qrScanner.destroy();
          } catch (err) {
            console.warn("Error cleaning up previous QR scanner:", err);
          }
          this.qrScanner = null;
        }

        this.qrScanner = await this.initializeCustomQrScanner();
        console.log("Custom QR scanner initialized and started");

        this.state.scanningQR = true;
      } catch (qrError) {
        const viewFinderConfig =
          this.config?.configs?.experience_config?.viewFinderConfig || {};
        const preScanText = viewFinderConfig.preScanText || TEXTS.SCAN_QR;

        this.updateToast(preScanText, "#242424", "#FFFFFF", "");
        console.error("Error initializing CustomQrScanner:", qrError);
      }
    } catch (error) {
      console.error("Error starting viewfinder:", error);

      if (this.errorCallbackFn) {
        this.errorCallbackFn({
          type: "CAMERA_ERROR",
          message: "Failed to start camera viewfinder",
          error,
        });
      }
    }
  }

  private createToast(
    text: string,
    background: string,
    color: string,
    icon: string
  ): void {
    const existingToast = document.querySelector(".toast-message-scan");
    if (existingToast) existingToast.remove();

    this.toast = document.createElement("div");
    this.toast.classList.add("toast-message-scan");

    this.toast.style.position = "fixed";
    this.toast.style.bottom = "auto";
    this.toast.style.transform = "translateX(-50%)";

    const textSpan = document.createElement("span");
    textSpan.innerHTML = text.replace(/\n/g, "<br>");
    textSpan.style.fontSize = "14px";
    if (icon) {
      const iconImg = document.createElement("img");
      iconImg.src = icon;
      iconImg.style.width = "18px";
      iconImg.style.height = "18px";
      iconImg.style.display = "inline-block";

      this.toast.appendChild(iconImg);
      this.toast.appendChild(textSpan);
    } else {
      this.toast.appendChild(textSpan);
    }

    document.body.appendChild(this.toast);
  }

  private updateToast(
  text: string,
  background: string,
  color: string,
  icon: string
): void {
  if (!text) return;
  const viewFinderConfig =
    this.config?.configs?.experience_config?.viewFinderConfig || {};
  const productDetectedText =
    viewFinderConfig.productDetected || TEXTS.QR_DETECTED;

  if (!text.includes(productDetectedText) && this.productDetectedTimer) {
    console.log(`Clearing timer - message changed to: "${text}"`);
    window.clearTimeout(this.productDetectedTimer);
    this.productDetectedTimer = null;
    this.productDetectedStartTime = 0;
    this.removeCentralCountdownTimer();
  }
  
  if (this.toast) {
    const textSpan = this.toast.querySelector("span");
    if (textSpan) {
      textSpan.innerHTML = text.replace(/\n/g, "<br>");
    }

    const existingIcon = this.toast.querySelector("img");
    if (icon && !existingIcon) {
      const iconImg = document.createElement("img");
      iconImg.src = icon;
      iconImg.style.width = "18px";
      iconImg.style.height = "18px";
      iconImg.style.display = "inline-block";
      this.toast.insertBefore(iconImg, textSpan);
    } else if (!icon && existingIcon) {
      existingIcon.remove();
    } else if (icon && existingIcon) {
      (existingIcon as HTMLImageElement).src = icon;
    }
  } else {
    this.createToast(text, background, color, icon);
  }
}

  private calculateQRPixelSize(cornerPoints: { x: number; y: number }[]): {
    width: number;
    height: number;
    avgSize: number;
    normalizedSize: number;
  } {
    if (cornerPoints.length !== 4) {
      return { width: 0, height: 0, avgSize: 0, normalizedSize: 0 };
    }

    const width = Math.sqrt(
      Math.pow(cornerPoints[1].x - cornerPoints[0].x, 2) +
        Math.pow(cornerPoints[1].y - cornerPoints[0].y, 2)
    );

    const height = Math.sqrt(
      Math.pow(cornerPoints[3].x - cornerPoints[0].x, 2) +
        Math.pow(cornerPoints[3].y - cornerPoints[0].y, 2)
    );

    const avgSize = (width + height) / 2;

    const videoWidth = this.videoElement.videoWidth || 1920;
    const videoHeight = this.videoElement.videoHeight || 1080;

    const standardWidth = 1920;
    const standardHeight = 1080;
    const resolutionScale = Math.sqrt(
      (standardWidth * standardHeight) / (videoWidth * videoHeight)
    );

    const normalizedSize = avgSize * resolutionScale;

    console.log("Pixel-Based QR Analysis:", {
      rawPixelSize: avgSize.toFixed(1) + "px",
      videoResolution: `${videoWidth}x${videoHeight}`,
      resolutionScale: resolutionScale.toFixed(3),
      normalizedSize: normalizedSize.toFixed(1) + "px",
      zoomLevel: this.zoomLevel,
      device: navigator.userAgent.includes("iPhone") ? "iOS" : "Android",
    });

    return { width, height, avgSize, normalizedSize };
  }

  private isQRSizeValid(cornerPoints: { x: number; y: number }[]): boolean {
    const sizeData = this.calculateQRPixelSize(cornerPoints);
    const normalizedSize = sizeData.normalizedSize;

    let { minWidth, maxWidth, minWidthIOS, maxWidthIOS } =
      this.autoCaptureConfig.qrPhysicalSize;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      minWidth = minWidthIOS;
      maxWidth = maxWidthIOS;
    }

    const isValid = normalizedSize >= minWidth && normalizedSize <= maxWidth;

    console.log("Pixel Size Validation:", {
      rawSize: sizeData.avgSize.toFixed(1) + "px",
      normalizedSize: normalizedSize.toFixed(1) + "px",
      minThreshold: minWidth + "px",
      maxThreshold: maxWidth + "px",
      isValid,
      videoRes: `${this.videoElement.videoWidth}x${this.videoElement.videoHeight}`,
      zoomApplied: this.zoomLevel > 1 ? `${this.zoomLevel}x` : "None",
    });

    return isValid;
  }

  private async triggerAutoCapture(): Promise<void> {
    if (this.isAutoCaptureInProgress) return;

    this.isAutoCaptureInProgress = true;

    try {
      const viewFinderConfig =
        this.config?.configs?.experience_config?.viewFinderConfig || {};
      const capturingText = viewFinderConfig.capturingText || "Capturing...";
      this.updateToast(capturingText, "#EB6F31", "#FFFFFF", "");

      if (this.posthogEvent) {
        this.posthogEvent.sendPostHogEvent("auto_capture_triggered", {
          stable_frames_count: this.qrDetectionBuffer.filter((d) => d.isValid)
            .length,
          timestamp: new Date().toISOString(),
        });
      }
      await this.handleCapture();
    } catch (error) {
      console.error("Error during auto capture:", error);
      this.isAutoCaptureInProgress = false;

      // Reset and continue scanning after error
      setTimeout(() => {
        if (!this.state.verifying) {
          this.isFirstQrDetection = true;
          this.qrDetectionBuffer = [];
        }
      }, 2000);
    }
  }

    private createCentralCountdownTimer(initialCount: number): HTMLElement {
    if (!document.head.querySelector('#countdown-timer-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'countdown-timer-styles';
      styleElement.innerHTML = VyuScanAutoSDK.COUNTDOWN_TIMER_STYLES;
      document.head.appendChild(styleElement);
    }

    const overlay = document.createElement('div');
    overlay.className = 'countdown-timer-overlay';
    overlay.id = 'central-countdown-timer';

    const container = document.createElement('div');
    container.className = 'countdown-timer-container';

    // Create SVG circle
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'countdown-progress-circle');
    svg.setAttribute('viewBox', '0 0 150 150');

    // Progress circle
    const progressCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    progressCircle.setAttribute('class', 'countdown-progress-bar');
    progressCircle.setAttribute('cx', '75');
    progressCircle.setAttribute('cy', '75');
    progressCircle.setAttribute('r', '70');

    svg.appendChild(progressCircle);

    // Create digit display
    const digit = document.createElement('div');
    digit.className = 'countdown-timer-digit';
    digit.textContent = initialCount.toString();

    container.appendChild(svg);
    container.appendChild(digit);
    overlay.appendChild(container);
    this.updateCentralCountdownTimer(overlay, initialCount);
    this.hideGridAndCorners();
    return overlay;
  }

  private hideGridAndCorners(): void {
    const gridContainer = document.getElementById("grid-container");
    if (gridContainer) gridContainer.style.display = "none";
    
    const cornerOverlay = document.getElementById("corner-overlay");
    if (cornerOverlay) cornerOverlay.style.display = "none";
  }

  private showGridAndCorners(): void {
    const gridContainer = document.getElementById("grid-container");
    if (gridContainer) gridContainer.style.display = "grid";
    
    const cornerOverlay = document.getElementById("corner-overlay");
    if (cornerOverlay) cornerOverlay.style.display = "block";
  }

  private updateCentralCountdownTimer(overlay: HTMLElement, count: number): void {
    const digit = overlay.querySelector('.countdown-timer-digit') as HTMLElement;
    const progressCircle = overlay.querySelector('.countdown-progress-bar') as SVGCircleElement;

    if (digit && progressCircle) {
      digit.textContent = count.toString();
      
      const circumference = 2 * Math.PI * 70;
      
      // Hard reset by cloning the element
      const newProgressCircle = progressCircle.cloneNode(true) as SVGCircleElement;
      newProgressCircle.style.strokeDashoffset = circumference.toString();
      newProgressCircle.style.transition = 'none';
      
      progressCircle.parentNode?.replaceChild(newProgressCircle, progressCircle);
      
      // Start animation
      requestAnimationFrame(() => {
        newProgressCircle.style.transition = 'stroke-dashoffset 1s linear';
        newProgressCircle.style.strokeDashoffset = '0';
      });
    }
  }

  private removeCentralCountdownTimer(hideGrid?: boolean): void {
    const timer = document.getElementById('central-countdown-timer');
    if (timer) {
      timer.remove();
      if (!hideGrid) {
        this.showGridAndCorners();
      }
    }
  }
  private handleAutoCapture(result: any): void {
    const now = Date.now();
    this.lastQrDetectionTime = now;

    if (this.isFirstQrDetection) {
      this.isFirstQrDetection = false;
    }

    const sizeData = this.calculateQRPixelSize(result.cornerPoints);
    const isWithinSizeRange = this.isQRSizeValid(result.cornerPoints);
    const isInCenter = this.isQRCentered(result.cornerPoints);

    console.log("Auto Capture Analysis:", {
      qrPixelSize: sizeData.avgSize.toFixed(1) + "px",
      normalizedSize: sizeData.normalizedSize.toFixed(1) + "px",
      sizeValid: isWithinSizeRange,
      centered: isInCenter,
      overall: isWithinSizeRange ? "VALID" : "INVALID",
    });

    this.qrDetectionBuffer.push({
      timestamp: now,
      qrData: result.data,
      cornerPoints: result.cornerPoints,
      physicalSize: { width: sizeData.width, height: sizeData.height },
      isValid: isWithinSizeRange,
    });

    const requiredFrames =
      this.autoCaptureConfig.stabilityConfig.requiredStableFrames;
    if (this.qrDetectionBuffer.length > requiredFrames) {
      this.qrDetectionBuffer.shift();
    }

    this.updatePixelBasedUI(isWithinSizeRange, isInCenter, sizeData);

    //const validDetections = this.qrDetectionBuffer.filter((d) => d.isValid);
    // if (
    //   validDetections.length >= requiredFrames &&
    //   this.qrDetectionBuffer.length === requiredFrames
    // ) {
    //   console.log("Pixel-based: Buffer stable, triggering capture");
    //   this.qrDetectionBuffer = [];
    //   this.triggerAutoCapture();
    // }
  }

  private isQRCentered(cornerPoints: { x: number; y: number }[]): boolean {
    try {
      if (!this.centerGridCell || cornerPoints.length !== 4) {
        return false;
      }

      const videoWidth = this.videoElement.videoWidth;
      const videoHeight = this.videoElement.videoHeight;
      const displayWidth = this.videoElement.offsetWidth;
      const displayHeight = this.videoElement.offsetHeight;

      const scaleX = displayWidth / videoWidth;
      const scaleY = displayHeight / videoHeight;

      const scaledPoints = cornerPoints.map((point) => ({
        x: point.x * scaleX,
        y: point.y * scaleY,
      }));

      const centerX = scaledPoints.reduce((sum, p) => sum + p.x, 0) / 4;
      const centerY = scaledPoints.reduce((sum, p) => sum + p.y, 0) / 4;

      const rect = this.centerGridCell.getBoundingClientRect();

      const basePadding = Math.min(rect.width, rect.height) * 0.2;
      const enterPadding = basePadding;
      const exitPadding = basePadding + 20;

      const padding = enterPadding;

      const centerInCell =
        centerX >= rect.left + padding &&
        centerX <= rect.right - padding &&
        centerY >= rect.top + padding &&
        centerY <= rect.bottom - padding;

      console.log("Strict center check:", {
        centerX: centerX.toFixed(1),
        centerY: centerY.toFixed(1),
        rect: {
          left: rect.left.toFixed(1),
          right: rect.right.toFixed(1),
          top: rect.top.toFixed(1),
          bottom: rect.bottom.toFixed(1),
          width: rect.width.toFixed(1),
          height: rect.height.toFixed(1),
        },
        strictBounds: {
          left: (rect.left + padding).toFixed(1),
          right: (rect.right - padding).toFixed(1),
          top: (rect.top + padding).toFixed(1),
          bottom: (rect.bottom - padding).toFixed(1),
        },
        padding: padding.toFixed(1),
        centerInCell,
      });

      return centerInCell;
    } catch (error) {
      console.error("Error in strict center check:", error);
      return false;
    }
  }

  private updatePixelBasedUI(
  isWithinSizeRange: boolean,
  isInCenter: boolean,
  sizeData: { avgSize: number; normalizedSize: number },
  showTimer: boolean = false
): void {
  if (this.state.verifying || this.isAutoCaptureInProgress) return;

  const viewFinderConfig =
    this.config?.configs?.experience_config?.viewFinderConfig || {};
  let message = "";
  let bgColor = "#242424";

  if (!isWithinSizeRange) {
    const { minWidth, maxWidth } = this.autoCaptureConfig.qrPhysicalSize;
    const currentNormalized = sizeData.normalizedSize;

    if (currentNormalized < minWidth) {
      message = viewFinderConfig.moveCloserText || TEXTS.MOVE_CLOSER;
      bgColor = "#FF6B00";
    } else if (currentNormalized > maxWidth) {
      message = viewFinderConfig.moveAwayText || TEXTS.MOVE_AWAY;
      bgColor = "#FF6B00";
    }
  }  else {
    // QR detected and centered
    if (!this.productDetectedTimer) {
      console.log("Starting 3-second timer with central countdown");
      this.productDetectedStartTime = Date.now();
      const countdownOverlay = this.createCentralCountdownTimer(3);
      document.body.appendChild(countdownOverlay);
      const productDetectedText = viewFinderConfig.productDetected || TEXTS.QR_DETECTED;
      this.updateToast(productDetectedText, "#FFC107", "#FFFFFF", "");
      let currentSecond = 3;
      this.countdownInterval = window.setInterval(() => {
        currentSecond--;
        
        if (currentSecond > 0) {
          this.updateCentralCountdownTimer(countdownOverlay, currentSecond);
        }
      }, 1000);
      
      this.productDetectedTimer = window.setTimeout(() => {
        console.log("Timer completed, triggering capture");
        this.removeCentralCountdownTimer(true);
        this.productDetectedTimer = null;
        this.productDetectedStartTime = 0;
        this.triggerAutoCapture();
      }, 3000);
    }

    return;
  }

  this.updateToast(message, bgColor, "#FFFFFF", "");
}


  private handleInvalidQR(result: any): void {
    console.log("Invalid QR code detected");

    this.state.qrMatched = false;

    if (this.qrUrl && result.data) {
      this.stopCameraAndScanners();
      this.createPreviewBackground();
      this.showResult("INVALID_QR");
    }
  }

  private handleQRDecodeError(error: any, qrScanner: any): void {
    const qrNonDetectionBufferBreakThreshold = this.autoCaptureConfig.stabilityConfig.qrNonDetectionBufferBreakThreshold || 1500;
    const qrNonDetectionBufferClearThreshold = this.autoCaptureConfig.stabilityConfig.qrNonDetectionBufferClearThreshold || 2500;
    if (this.state.scanningQR && !this.state.verifying) {
      if (error.toString().includes("timeout")) {
        console.warn(" QR Scanner timeout detected, attempting restart...");
        this.handleScannerTimeout(qrScanner);
        return;
      }
      if (!error.includes("No QR code found")) {
        console.error("QR scanning error:", error);
      }

      const now = Date.now();
        //Add failed detection to existing buffer
        this.qrDetectionBuffer.push({
          timestamp: now,
          qrData: "",
          cornerPoints: [],
          physicalSize: { width: 0, height: 0 },
          isValid: false,
        });

        //Keep buffer size manageable
        const requiredFrames =
          this.autoCaptureConfig.stabilityConfig.requiredStableFrames;
        if (this.qrDetectionBuffer.length > requiredFrames) {
          this.qrDetectionBuffer.shift();
        }
        if (now - this.lastQrDetectionTime > qrNonDetectionBufferBreakThreshold) {
          this.qrDetectionBuffer.push({
            timestamp: now,
            qrData: "",
            cornerPoints: [],
            physicalSize: { width: 0, height: 0 },
            isValid: false,
          });
          // Keep buffer size manageable
          const requiredFrames =
            this.autoCaptureConfig.stabilityConfig.requiredStableFrames;
          if (this.qrDetectionBuffer.length > requiredFrames) {
            this.qrDetectionBuffer.shift();
          }
        }
        // Show pre-scan text after 3 seconds
        if (now - this.lastQrDetectionTime > qrNonDetectionBufferClearThreshold) {
          console.log(`-No QR detection for ${qrNonDetectionBufferClearThreshold / 1000}s, showing pre-scan text`);
          this.isFirstQrDetection = true;
          this.qrDetectionBuffer = [];
          this.removeCentralCountdownTimer();
          if (this.state.scanningQR && !this.state.verifying) {
            const viewFinderConfig =
              this.config?.configs?.experience_config?.viewFinderConfig || {};
            const preScanText = viewFinderConfig.preScanText || TEXTS.SCAN_QR;
            this.updateToast(preScanText, "#242424", "#FFFFFF", "");
          }
        }
        return;
    }
  }

  private async startCamera(): Promise<void> {
    try {
      //await new Promise((resolve) => setTimeout(resolve, 5000));
      if (this.stream) {
        this.stream.getTracks().forEach((track) => {
          console.log("Stopping existing track:", track.kind, track.id);
          track.stop();
        });
        this.stream = null;
      }

      if (this.videoElement && this.videoElement.srcObject) {
        console.log("Clearing existing video source");
        this.videoElement.srcObject = null;
      }

      if (this.videoElement) {
        console.log("Configuring video element...");
        this.videoElement.setAttribute("autoplay", "true");
        this.videoElement.setAttribute("playsinline", "true");
        this.videoElement.setAttribute("muted", "true");
        this.videoElement.muted = true;
        this.videoElement.style.display = "block";
        this.videoElement.style.backgroundColor = "#000";
        this.videoElement.style.width = "100vw";
        this.videoElement.style.height = "100vh";
        this.videoElement.style.objectFit = "cover";
      }

      console.log("Getting available cameras...");

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );

      console.log(
        `Found ${videoDevices.length} video devices:`,
        videoDevices.map((d) => ({
          deviceId: d.deviceId.substring(0, 8) + "...",
          label: d.label || "unnamed",
        }))
      );

      let selectedDeviceId: string | null = null;

      if (videoDevices.length > 0) {
        console.log("Searching for back camera among available devices...");

        for (const device of videoDevices) {
          const label = device.label.toLowerCase();
          console.log(`Examining camera: ${label}`);

          const isBackCamera = BACK_INDICATORS.some((indicator) =>
            label.includes(indicator.toLowerCase())
          );

          const isExcluded = EXCLUDE_INDICATORS.some((indicator) =>
            label.includes(indicator.toLowerCase())
          );

          if (isBackCamera && !isExcluded) {
            console.log(`Found likely back camera: ${label}`);
            selectedDeviceId = device.deviceId;
          }
        }

        if (!selectedDeviceId) {
          console.log(
            "No back camera identified by label, using first available camera"
          );
          selectedDeviceId = videoDevices[0].deviceId;
        }
      }

      console.log(
        `Selected camera device ID: ${selectedDeviceId?.substring(0, 8)}...`
      );
      this.selectedDeviceCameraId = selectedDeviceId;

      let capabilities: MediaTrackCapabilities | null = null;

      console.log("Getting temporary stream to check capabilities...");
      let tempStream: MediaStream | null = null;

      try {
        const tempConstraints = {
          video: selectedDeviceId
            ? { deviceId: { exact: selectedDeviceId } }
            : { facingMode: { ideal: "environment" } },
          audio: false,
        };
        tempStream = await navigator.mediaDevices.getUserMedia(tempConstraints);
        const tempTrack = tempStream.getVideoTracks()[0];
        capabilities = tempTrack.getCapabilities();

        console.log(" Camera capabilities discovered:", capabilities);
      } catch (tempError) {
        console.warn(
          " Could not get temporary stream for capabilities:",
          tempError
        );
      } finally {
        if (tempStream) {
          console.log(" Stopping temporary stream to free camera resources");
          tempStream.getTracks().forEach((track) => {
            console.log(
              `  - Stopping temp track: ${track.kind} (${track.label})`
            );
            track.stop();
          });
          tempStream = null;
        }
      }

      const isAndroid = /Android/i.test(navigator.userAgent);
      console.log(
        `📱 Platform detected: ${isAndroid ? "Android" : "iOS/Other"}`
      );

      const optimalConstraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: "environment" },
          ...(selectedDeviceId && { deviceId: { exact: selectedDeviceId } }),
        },
        audio: false,
      };

      if (capabilities) {
        console.log("Building optimal constraints based on capabilities...");

        if (
          capabilities.focusMode &&
          Array.isArray(capabilities.focusMode) &&
          capabilities.focusMode.includes("continuous")
        ) {
          (optimalConstraints.video as any).focusMode = { ideal: "continuous" };
          console.log("   Focus mode: continuous");
        }

        if (capabilities.zoom && this.zoomLevel > 1) {
          const maxZoom = capabilities.zoom.max || 1;
          const minZoom = capabilities.zoom.min || 1;
          const targetZoom = Math.min(
            Math.max(this.zoomLevel, minZoom),
            maxZoom
          );

          if (targetZoom !== this.zoomLevel) {
            console.log(
              `  Zoom adjusted from ${this.zoomLevel} to ${targetZoom} (within camera limits: ${minZoom}-${maxZoom})`
            );
            this.zoomLevel = targetZoom;
          }

          (optimalConstraints.video as any).zoom = isAndroid
            ? { ideal: targetZoom }
            : { ideal: targetZoom };

          console.log(
            `  Zoom: ${targetZoom}x (${isAndroid ? "exact" : "ideal"} for ${
              isAndroid ? "Android" : "iOS"
            })`
          );
        }

        if (capabilities.width && capabilities.height) {
          const maxWidth = capabilities.width.max;
          const maxHeight = capabilities.height.max;

          if (maxWidth && maxHeight) {
            if (isAndroid) {
              const resWidth = 2160;
              const resHeight = 1440;
              (optimalConstraints.video as any).width = {
                min: resWidth,
                ideal: resWidth,
                max: resWidth,
              };
              (optimalConstraints.video as any).height = {
                min: resHeight,
                ideal: resHeight,
                max: resHeight,
              };
              console.log(
                `  Android Resolution: ${resWidth}x${resHeight} (min/ideal/max)`
              );
            } else {
              (optimalConstraints.video as any).width = { ideal: maxWidth };
              (optimalConstraints.video as any).height = { ideal: maxHeight };
              console.log(
                `  Full Resolution: ${maxWidth}x${maxHeight} (ideal)`
              );
            }
          }
        }
      } else {
        console.log(
          " No capabilities available - using basic constraints only"
        );
      }

      let stream: MediaStream | null = null;

      console.log("Getting final optimized stream...");
      console.log(
        "Final constraints:",
        JSON.stringify(optimalConstraints, null, 2)
      );

      try {
        stream = await navigator.mediaDevices.getUserMedia(optimalConstraints);
        console.log(
          "Optimal stream acquired successfully with all optimizations"
        );
      } catch (optimalError) {
        console.warn("⚠️ Optimal constraints failed:", optimalError.message);

        const qualityTiers = [
          { width: 1920, height: 1080, name: "1080p" },
          { width: 1280, height: 720, name: "720p" },
          { width: 854, height: 480, name: "480p" },
          { width: 640, height: 480, name: "VGA" },
        ];

        let tierSuccessful = false;

        for (let i = 0; i < qualityTiers.length; i++) {
          const tier = qualityTiers[i];

          try {
            const tierConstraints = { ...optimalConstraints };
            const videoConstraints = tierConstraints.video as any;

            if (isAndroid) {
              videoConstraints.width = {
                ideal: tier.width,
              };
              videoConstraints.height = {
                ideal: tier.height,
              };
            } else {
              videoConstraints.width = { ideal: tier.width };
              videoConstraints.height = { ideal: tier.height };
            }

            console.log(
              `🔄 Trying quality tier ${i + 1}: ${tier.name} (${tier.width}x${
                tier.height
              })...`
            );
            console.log("tier constraints", tierConstraints);
            stream = await navigator.mediaDevices.getUserMedia(tierConstraints);
            console.log(`Quality tier ${tier.name} successful!`);
            tierSuccessful = true;
            break;
          } catch (tierError) {
            console.warn(
              `⚠️ Quality tier ${tier.name} failed:`,
              tierError.message
            );
            continue;
          }
        }

        if (!tierSuccessful) {
          try {
            const fallbackConstraints = { ...optimalConstraints };
            delete (fallbackConstraints.video as any).zoom;

            console.log(
              "Trying fallback without zoom (original resolution)..."
            );
            stream = await navigator.mediaDevices.getUserMedia(
              fallbackConstraints
            );
            console.log(" Fallback stream acquired (without zoom)");
            tierSuccessful = true;
          } catch (fallbackError) {
            console.warn(
              "Fallback without zoom failed:",
              fallbackError.message
            );
          }
        }

        if (!tierSuccessful) {
          try {
            const minimalConstraints: MediaStreamConstraints = {
              video: selectedDeviceId
                ? { deviceId: { exact: selectedDeviceId } }
                : { facingMode: { ideal: "environment" } },
              audio: false,
            };

            console.log("Trying minimal constraints as final fallback...");
            stream = await navigator.mediaDevices.getUserMedia(
              minimalConstraints
            );
            console.log("Minimal stream acquired");
          } catch (minimalError) {
            console.error(
              "❌ All stream acquisition attempts failed:",
              minimalError
            );
            throw minimalError;
          }
        }
      }

      if (!stream || stream.getVideoTracks().length === 0) {
        throw new Error("No video tracks available in final stream");
      }

      const videoTrack = stream.getVideoTracks()[0];
      console.log("📹 Final camera acquired:", videoTrack.label);

      this.stream = stream;
      this.videoElement.srcObject = stream;

      this.videoElement.onloadedmetadata = () => {
        console.log(
          "Video metadata loaded, dimensions:",
          this.videoElement.videoWidth,
          "x",
          this.videoElement.videoHeight
        );
        if (this.updateCanvasSize) {
          this.updateCanvasSize();
        }
      };

      try {
        await this.videoElement.play();
        console.log("Video element playback started successfully");
      } catch (playError) {
        console.warn("Play error (non-fatal):", playError);
      }

      const settings = videoTrack.getSettings();
      console.log("Final camera settings:", {
        label: videoTrack.label,
        width: settings.width,
        height: settings.height,
        frameRate: settings.frameRate || "not limited",
        facingMode: settings.facingMode || "not reported",
        focusMode: settings.focusMode || "not reported",
        zoom: settings.zoom || "not supported/applied",
      });

      console.log(" Camera initialization completed successfully!");
      return;
    } catch (error) {
      console.error("Camera start critical error:", error);
      throw error;
    }
  }

  public async restartCamera(): Promise<void> {
    console.log("Restarting camera");

    try {
      if (this.qrScanner) {
        try {
          console.log("Cleaning up existing QR scanner instance");
          if (typeof this.qrScanner.stop === "function") {
            this.qrScanner.stop();
          }
          if (typeof this.qrScanner.destroy === "function") {
            this.qrScanner.destroy();
          }
        } catch (err) {
          console.warn("Error during QR scanner cleanup:", err);
        }
        this.qrScanner = null;
      }

      this.resetQrCenterStateDebounce();
      this.state.scanningQR = false;
      this.state.qrInCenter = false;
      this.state.qrMatched = false;

      // Reset auto capture state
      this.qrDetectionBuffer = [];
      this.isAutoCaptureInProgress = false;
      if (this.autoCaptureTimeout) {
        window.clearTimeout(this.autoCaptureTimeout);
        this.autoCaptureTimeout = null;
      }

      if (this.stream) {
        this.stream.getTracks().forEach((track) => {
          try {
            console.log(`Stopping track: ${track.kind} (${track.label})`);
            track.stop();
          } catch (err) {
            console.warn("Error stopping track:", err);
          }
        });
        this.stream = null;
      }

      if (this.videoElement && this.videoElement.srcObject) {
        console.log("Clearing video source");
        this.videoElement.srcObject = null;
      }

      console.log("Starting fresh camera stream");

      await new Promise((resolve) => setTimeout(resolve, 500));

      await this.startCamera();

      if (!this.stream || !this.videoElement.srcObject) {
        throw new Error("Failed to get camera stream during restart");
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      await this.reconnectServices();

      this.initializeCustomQrScanner();
    } catch (error) {
      console.error("Critical error during camera/scanner restart:", error);

      try {
        console.log("LAST RESORT: Attempting to run startViewfinder");

        if (this.qrScanner) {
          try {
            this.qrScanner.stop();
          } catch (e) {}
          try {
            this.qrScanner.destroy();
          } catch (e) {}
          this.qrScanner = null;
        }

        await this.startViewfinder();
      } catch (finalError) {
        console.error("All restart attempts failed:", finalError);
        this.updateToast(
          "Scanner error. Please try again.",
          "#BE2705",
          "#FFFFFF",
          ""
        );
      }
    }
  }

  private cleanupScanRegionHighlights(): void {
    console.log("Cleaning up scan region highlights");
    
    const highlights = document.querySelectorAll('.scan-region-highlight, .code-outline-highlight');
    highlights.forEach((element) => {
      console.log("Removing highlight element:", element.className);
      element.remove();
    });
    
    const scannerSvgs = document.querySelectorAll('svg[style*="scan"]');
    scannerSvgs.forEach((svg) => {
      if (svg.parentElement && svg.parentElement.classList.contains('scan-region-highlight')) {
        svg.parentElement.remove();
      }
    });
  }

  private async reconnectServices(): Promise<void> {
    console.log("Reconnecting services after resume");
    
    try {
      if (!this.wsClient || this.wsClient.readyState !== WebSocket.OPEN) {
        console.log("Re-establishing WebSocket connection");
        await this.initializeWebSocket();
        if (this.sessionId) {
          await this.setWSSessionId(this.sessionId);
        }
      } else {
        console.log("WebSocket still connected");
      }
      
      if (!this.mqttClient || !this.mqttClient.connected) {
        console.log("Re-establishing MQTT connection");
        if (this.sessionId) {
          await this.initializeMQTTAsync(this.sessionId);
          await this.subscribeToImageResults();
        }
      } else {
        console.log("MQTT still connected");
      }
      
      console.log("Service reconnection completed successfully");
    } catch (error) {
      console.error("Error reconnecting services:", error);
      throw error;
    }
  }

  private closeAllConnections(): void {
    console.log("Closing all network connections");
    
    if (this.wsClient) {
      try {
        console.log("Closing WebSocket connection...");
        if (this.wsClient.readyState === WebSocket.OPEN) {
          this.wsClient.close(1000, "SDK cleanup");
        }
        this.wsClient = null;
        this.wsMessageHandlers.clear();
        console.log("WebSocket connection closed.");
      } catch (error) {
        console.error("Error closing WebSocket connection:", error);
      }
    }

    if (this.mqttClient) {
      try {
        if (this.mqttClient.connected) {
          this.mqttClient.unsubscribe(
            `${this.sessionId}-session-scan-result-${this.environment}`,
            (err) => {
              if (!err) {
                console.log(`Successfully unsubscribed from topic: ${this.sessionId}-session-scan-result-${this.environment}`);
              } else {
                console.error(`Failed to unsubscribe from topic: ${this.sessionId}-session-scan-result-${this.environment}`, err);
              }
            }
          );
        }

        if (this.verificationTimeoutId) {
          window.clearTimeout(this.verificationTimeoutId);
          this.verificationTimeoutId = null;
        }

        if (this.mqttSessionExpireTopic && this.mqttClient.connected) {
          this.mqttClient.publish(this.mqttSessionExpireTopic, this.sessionId);
        }

        this.mqttClient.removeListener("message", this.messageHandler);
        this.mqttClient.off("message", this.messageHandler);

        this.mqttClient.end(false, () => {
          console.log("MQTT client disconnected.");
        });

        this.mqttClient = null;
      } catch (error) {
        console.error("Error closing MQTT connection:", error);
      }
    }
  }

  private async startScannerWithRetry(
    scanner: QrScanner,
    maxRetries = 3
  ): Promise<void> {
    let attempt = 0;
    let success = false;

    while (attempt < maxRetries && !success) {
      try {
        attempt++;
        console.log(`Starting QR scanner - attempt ${attempt}/${maxRetries}`);
        try {
          scanner.stop();
        } catch (e) {}

        await scanner.start();
        success = true;
        console.log("QR scanner started successfully");
      } catch (error) {
        console.warn(`QR scanner start failed on attempt ${attempt}:`, error);

        if (attempt < maxRetries) {
          const delay = 500 * attempt;
          console.log(`Retrying after ${delay}ms...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          console.error(
            `Failed to start QR scanner after ${maxRetries} attempts`
          );
          throw error;
        }
      }
    }
  }

  private validateQR(qrData: string): boolean {
    console.log("qrUrl-->validateQR:", this.qrUrl);
    qrData = qrData.substring(qrData.lastIndexOf("/") + 1);
    console.log("qrdata-->validateQR:", qrData);
    if (!this.qrUrl) return false;
    return qrData === this.qrUrl;
  }

  private async loadQrScanner(): Promise<any> {
    return new Promise((resolve, reject) => {
      console.log("Loading QR scanner UMD from public directory");

      const script = document.createElement("script");
      script.src = "/lib/qr-scanner.umd.min.js";
      script.type = "text/javascript";

      script.onload = () => {
        console.log("QR scanner UMD script loaded");

        setTimeout(() => {
          if ((window as any).QrScanner) {
            const QrScanner = (window as any).QrScanner;
            QrScanner.WORKER_PATH = "/lib/qr-scanner-worker.min.js";
            console.log("QR scanner UMD loaded successfully");
            resolve(QrScanner);
          } else {
            reject(new Error("QrScanner not found in global scope"));
          }
        }, 200);
      };

      script.onerror = (error) => {
        console.error("Failed to load QR scanner UMD script:", error);
        reject(new Error(`Script loading failed: ${error}`));
      };

      document.head.appendChild(script);
    });
  }

  private async initializeCustomQrScanner(): Promise<any> {
    console.log(
      "Initializing custom QR scanner implementation with auto capture support"
    );
    const maxScansPerSecond = this.autoCaptureConfig?.maxScansPerSecond || 10;
    try {
      const QrScanner = await this.loadQrScanner();
      
      let preferredDeviceId = "environment";
      if (this.selectedDeviceCameraId) {
        preferredDeviceId = this.selectedDeviceCameraId;
      } else if (this.stream && this.stream.getVideoTracks().length > 0) {
        const currentTrack = this.stream.getVideoTracks()[0];
        const settings = currentTrack.getSettings();
        console.log("Current camera settings:", settings);
        if (settings.deviceId) {
          preferredDeviceId = settings.deviceId;
          console.log("Using camera device ID:", preferredDeviceId);
        }
      }

      this.resetQrCenterStateDebounce();
      console.log("QR center state debounce initialized");

      // Reset auto capture state
      this.qrDetectionBuffer = [];
      this.isAutoCaptureInProgress = false;
      console.log("Auto capture state reset");

      const qrScanner = new QrScanner(
        this.videoElement,
        (result) => {
          if (this.state.verifying) return;

          console.log(
            `QR detection: data="${result.data.substring(
              0,
              20
            )}..." cornerPoints=${result.cornerPoints.length}`
          );

          const isValidQR = this.validateQR(result.data);
          console.log(`QR validation: ${isValidQR ? "VALID" : "INVALID"}`);

          const now = Date.now();
          const isStateChange =
            this.lastQrDetectionEvent?.qrData !== result.data ||
            this.lastQrDetectionEvent?.isExpected !== isValidQR;

          if (isStateChange && this.posthogEvent) {
            this.posthogEvent.sendPostHogEvent("qr_detected_in_viewfinder", {
              detected_qr_data: result.data,
              is_expected_qr: isValidQR,
              capture_mode: "auto",
              timestamp: new Date().toISOString(),
            });

            this.lastQrDetectionEvent = {
              qrData: result.data,
              timestamp: now,
              isExpected: isValidQR,
            };
          }

          if (isValidQR) {
            this.state.qrMatched = true;
            this.handleAutoCapture(result);
          } else {
            this.handleInvalidQR(result);
          }
        },
        {
          onDecodeError: (error) => {
            this.handleQRDecodeError(error, qrScanner);
          },
          preferredCamera: preferredDeviceId,
          maxScansPerSecond: maxScansPerSecond,
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          //highlightCodeOutline: true,
          calculateScanRegion: (video) => {
            // Get the smaller dimension to create a square
            const smallerDimension = Math.min(
              video.videoWidth,
              video.videoHeight
            );
            // Adjust this percentage based on your needs (0.3 = 30% of smaller dimension)
            const scanPercentage = this.autoCaptureConfig?.scanRegionPercentage || 0.5;
            const scanSize = smallerDimension * scanPercentage;
            const centerX = (video.videoWidth - scanSize) / 2;
            const centerY = (video.videoHeight - scanSize) / 2;

            return {
              x: Math.round(centerX),
              y: Math.round(centerY),
              width: Math.round(scanSize),
              height: Math.round(scanSize)
            };
          },
        }
      );
      qrScanner.setInversionMode("both");
      await qrScanner.start();
      await this.startScannerWithRetry(qrScanner);

      const refreshInterval = window.setInterval(() => {
        if (this.state.verifying || this.state.resultsShown) return;

        if (qrScanner) {
          try {
            qrScanner.stop();
            qrScanner.start();
          } catch (err) {
            console.warn("Error during scanner refresh:", err);
          }
        }

        const now = Date.now();
        const qrNonDetectionBufferClearThreshold = this.autoCaptureConfig.stabilityConfig.qrNonDetectionBufferClearThreshold || 2500;
        if (
          this.state.qrInCenter &&
          now - this.qrCenterStateDebounce.stateChangeTime > qrNonDetectionBufferClearThreshold
        ) {
          console.log(
            `No QR detection for ${qrNonDetectionBufferClearThreshold / 1000}s while UI shows centered - updating UI`
          );
          this.state.qrInCenter = false;
          this.updateUiForQrCenterState(false);
        }

        // UI state is handled automatically for auto-capture mode
      }, 10000);

      if (this.qrCenterStateDebounce) {
        if (this.qrCenterStateDebounce.refreshIntervalId) {
          window.clearInterval(this.qrCenterStateDebounce.refreshIntervalId);
        }
        this.qrCenterStateDebounce.refreshIntervalId = refreshInterval;
      }

      return qrScanner;
    } catch (error) {
      console.error("Failed to initialize custom QR scanner:", error);

      const viewFinderConfig =
        this.config?.configs?.experience_config?.viewFinderConfig || {};
      const preScanText = viewFinderConfig.preScanText || TEXTS.SCAN_QR;
      this.updateToast(preScanText, "#242424", "#FFFFFF", "");

      throw error;
    }
  }
  

  private handleScannerTimeout(scanner: QrScanner): void {
    console.log("🔄 Handling scanner timeout - attempting recovery");

    if (
      this.scannerTimeoutDebounce &&
      Date.now() - this.scannerTimeoutDebounce < 3000
    ) {
      console.log("🔄 Timeout recovery already attempted recently, skipping");
      return;
    }

    this.scannerTimeoutDebounce = Date.now();

    try {
      setTimeout(async () => {
        try {
          console.log("🔄 Restarting QR scanner after timeout");
          await scanner.start();
          console.log("✅ QR scanner restarted successfully");
        } catch (restartError) {
          console.error(
            "❌ Failed to restart scanner, attempting full reinitialize"
          );
          this.reinitializeScanner();
        }
      }, 1000);
    } catch (error) {
      console.error("Error during timeout handling:", error);
      this.reinitializeScanner();
    }
  }

  private async reinitializeScanner(): Promise<void> {
    console.log("🔄 Full scanner reinitialization");

    try {
      if (this.qrScanner) {
        try {
          this.qrScanner.stop();
          this.qrScanner.destroy();
        } catch (e) {
          console.warn("Error cleaning up scanner:", e);
        }
        this.qrScanner = null;
      }

      this.qrScanner = await this.initializeCustomQrScanner();
      console.log("Scanner fully reinitialized");
    } catch (error) {
      console.error("Full reinitialize failed:", error);
    }
  }



  private updateUiForQrCenterState(isInCenter: boolean): void {
    if (this.state.verifying) return;

    const viewFinderConfig =
      this.config?.configs?.experience_config?.viewFinderConfig || {};

    console.log(
      `Direct UI update called: isInCenter=${isInCenter}, qrMatched=${this.state.qrMatched}`
    );

    if (this.posthogEvent) {
      this.posthogEvent.sendPostHogEvent("capture_button_state_changed", {
        new_state: isInCenter && this.state.qrMatched ? "enabled" : "disabled",
        reason_for_disablement: !this.state.qrMatched
          ? "no_qr_detected"
          : !isInCenter
          ? "qr_not_centered"
          : undefined,
        timestamp: new Date().toISOString(),
      });
    }

    if (isInCenter && this.state.qrMatched) {
      console.log("Valid QR code in center - ready for auto capture");

      // Auto capture SDK handles capture automatically - no manual buttons

      const focusText = viewFinderConfig.focusText || TEXTS.FOCUS_QR;
      this.updateToast(focusText, "#242424", "#FFFFFF", "");
    } else {
      console.log(
        `QR not centered or not matched - waiting for proper positioning (centered: ${isInCenter}, matched: ${this.state.qrMatched})`
      );

      // Auto capture SDK doesn't use manual capture buttons

      if (!isInCenter && this.state.qrMatched) {
        if (!this.state.verifying) {
          const preScanText = viewFinderConfig.preScanText || TEXTS.SCAN_QR;
          this.updateToast(preScanText, "#242424", "#FFFFFF", "");
        }
      }
    }
  }

  private resetQrCenterStateDebounce(): void {
    console.log("Performing thorough reset of QR center state tracking");
    this.clearCaptureTimers();
    if (this.qrCenterStateDebounce.timeoutId !== null) {
      window.clearTimeout(this.qrCenterStateDebounce.timeoutId);
    }

    if (this.qrCenterStateDebounce.refreshIntervalId) {
      window.clearInterval(this.qrCenterStateDebounce.refreshIntervalId);
    }
    if (this.stableBufferDebounceTimeout) {
      window.clearTimeout(this.stableBufferDebounceTimeout);
      this.stableBufferDebounceTimeout = null;
    }

    this.qrCenterStateDebounce = {
      lastState: false,
      stateChangeTime: 0,
      stabilityThreshold: 250,
      timeoutId: null,
      lastUiUpdateTime: Date.now(),
    };

    this.scannerTimeoutDebounce = 0;
    this.state.qrInCenter = false;
    this.state.qrMatched = false;
    this.lastQrDetectionTime = 0;
    this.isFirstQrDetection = true;
    this.qrDetectionBuffer = [];
  }



  private stopCameraAndScanners(): void {
    if (this.qrScanner) {
      try {
        console.log("Stopping QR scanner when showing results");
        this.qrScanner.stop();
        this.qrScanner.destroy();
        this.qrScanner = null;
      } catch (err) {
        console.warn("Error while stopping QR scanner:", err);
      }
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        console.log(`Stopping camera track: ${track.kind}`);
        track.stop();
      });
      this.stream = null;
    }

    if (this.videoElement && this.videoElement.srcObject) {
      this.videoElement.srcObject = null;
      this.videoElement.style.display = "none";
    }

    // Reset auto capture state
    this.qrDetectionBuffer = [];
    this.isAutoCaptureInProgress = false;
    if (this.autoCaptureTimeout) {
      window.clearTimeout(this.autoCaptureTimeout);
      this.autoCaptureTimeout = null;
    }

    this.state.scanningQR = false;
    this.state.qrInCenter = false;
    this.state.qrMatched = false;

    const gridContainer = document.getElementById("grid-container");
    if (gridContainer) {
      gridContainer.style.display = "none";
    }

    const cornerOverlay = document.getElementById("corner-overlay");
    if (cornerOverlay) {
      cornerOverlay.style.display = "none";
    }

  }

  private async handleCapture(): Promise<void> {
    try {
      this.state.verifying = true;
      this.state.scanningQR = false;
      this.state.qrInCenter = false;
      // Reset auto capture state
      this.isAutoCaptureInProgress = false;
      this.qrDetectionBuffer = [];

      const viewFinderConfig =
        this.config?.configs?.experience_config?.viewFinderConfig || {};
      const verifyingText = viewFinderConfig.verifyingText || TEXTS.VERIFYING;
      this.updateToast(verifyingText, "#242424", "#FFFFFF", "");
      this.createPulsatingBubbles();

      if (this.posthogEvent) {
        this.posthogEvent.sendPostHogEvent("verification_in_progress_viewed", {
          capture_mode: "auto",
          timestamp: new Date().toISOString(),
        });
      }

      if (this.qrScanner) {
        this.qrScanner.stop();
      }

      await this.captureImages();
      this.createPreviewBackground();
      this.stopCameraAndScanners();
    } catch (error) {
      console.error("Error during capture process:", error);

      //this.state.verifying = false;
      this.isAutoCaptureInProgress = false;
      this.state.error = error as Error;

      if (this.errorCallbackFn) {
        this.errorCallbackFn({
          type: "VERIFICATION_ERROR",
          message: "Failed to verify product",
          error,
        });
      }
    }
  }

  
  private clearCaptureTimers(): void {
    if (this.countdownInterval) {
      window.clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    if (this.productDetectedTimer) {
      window.clearTimeout(this.productDetectedTimer);
      this.productDetectedTimer = null;
      this.productDetectedStartTime = 0;
      this.removeCentralCountdownTimer();
    }
  }

  private resizeCanvasFor2K(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const maxDimension = 2048;

    if (canvas.width <= maxDimension && canvas.height <= maxDimension) {
      return canvas;
    }

    const scale = maxDimension / Math.max(canvas.width, canvas.height);
    const newWidth = Math.floor(canvas.width * scale);
    const newHeight = Math.floor(canvas.height * scale);

    const newCanvas = document.createElement("canvas");
    const ctx = newCanvas.getContext("2d")!;
    newCanvas.width = newWidth;
    newCanvas.height = newHeight;
    ctx.drawImage(canvas, 0, 0, newWidth, newHeight);

    console.log(
      `Resized image from ${canvas.width}x${canvas.height} to ${newWidth}x${newHeight}`
    );
    return newCanvas;
  }
  

  private async captureImages(): Promise<void> {
    try {
      this.state.uploadingImages = true;
      const capturedBlobs: Blob[] = [];

      const originalCanvas = document.createElement("canvas");
      const ctx = originalCanvas.getContext("2d");

      if (!ctx) {
        throw new Error("Unable to get canvas context");
      }

      originalCanvas.width = this.videoElement.videoWidth;
      originalCanvas.height = this.videoElement.videoHeight;
      ctx.drawImage(
        this.videoElement,
        0,
        0,
        originalCanvas.width,
        originalCanvas.height
      );

      // Apply resizing if enabled
      const canvasToUpload = this.shouldResizeImage ? this.resizeCanvasFor2K(originalCanvas) : originalCanvas;

      const dataUrl = canvasToUpload.toDataURL("image/jpeg", 1.0);
      this.previewImage = dataUrl;
      this.createPreviewBackground();

      // Log analytics for image capture
      if (this.posthogEvent) {
        this.posthogEvent.sendPostHogEvent("image_capture_attempted", {
          capture_mode: "auto",
          timestamp: new Date().toISOString(),
        });
      }

      // Store first blob for HTTP API fallback
      const firstBlob = await this.canvasToBlob(canvasToUpload);
      capturedBlobs.push(firstBlob);

      if (this.posthogEvent) {
        this.posthogEvent.sendPostHogEvent("image_upload_started", {
          timestamp: new Date().toISOString(),
          image_size_kb: Math.round(firstBlob.size / 1024),
          capture_mode: "auto",
        });
      }
      
      if (!this.sessionId) {
        console.log("Session ID not ready, waiting...");
        const maxWaitTime = 10000; 
        const startTime = Date.now();
        
        while (!this.sessionId && (Date.now() - startTime) < maxWaitTime) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        if (!this.sessionId) {
          throw new Error("Session not ready after timeout");
        }
        console.log("Session ID ready, proceeding with upload");
      }
      
      // Upload first image via WebSocket
      console.log("Uploading image via WebSocket");
      const uploadResult = await this.uploadImageViaWebSocket(dataUrl);
      console.log("First image uploaded via WebSocket:", uploadResult);
      this.imageCountUploaded++;
      
      // Note: Verification results will come via MQTT subscription

      // Upload additional images via WebSocket
      for (let i = 1; i < this.imageCount; i++) {
        try {
          // Create additional images
          const additionalCanvas = document.createElement("canvas");
          const additionalCtx = additionalCanvas.getContext("2d");

          if (!additionalCtx) {
            throw new Error("Unable to get canvas context for additional image");
          }

          additionalCanvas.width = this.videoElement.videoWidth;
          additionalCanvas.height = this.videoElement.videoHeight;
          additionalCtx.drawImage(
            this.videoElement,
            0,
            0,
            additionalCanvas.width,
            additionalCanvas.height
          );

          // Apply resizing if enabled
          const additionalCanvasToUpload = this.shouldResizeImage ? this.resizeCanvasFor2K(additionalCanvas) : additionalCanvas;
          const additionalDataUrl = additionalCanvasToUpload.toDataURL("image/jpeg", 1.0);
          
          await this.uploadImageViaWebSocket(additionalDataUrl);
          console.log(`Image ${i + 1} uploaded via WebSocket`);
          this.imageCountUploaded++;
        } catch (error) {
          console.error(`Error uploading image ${i + 1}:`, error);
          throw error;
        }
      }

      this.state.uploadingImages = false;
    } catch (error) {
      console.error("Error capturing images:", error);
      this.state.uploadingImages = false;
      if (this.imageCountUploaded === 0) {
        this.verifyProduct(5000);
      }
      throw error;
    }
  }


  private async verifyProduct(timeout?: number): Promise<void> {
    try {
      if (this.mqttClient && this.mqttClient.connected) {
        console.log("Within verify product. Timeout : ", timeout);

        const timeoutPromise = new Promise<void>((_, reject) => {
          this.verificationTimeoutId = window.setTimeout(() => {
            console.log("Timout Promise Started: ", timeout);
            if (this.state.verifying && !this.state.resultsShown) {
              console.error("MQTT verification timed out after 30 seconds");
              this.state.verifying = false;
              this.removeToast();
              this.showResult("ERROR");
              this.verificationTimeoutId = null;
              reject(new Error("Verification timed out"));
            }
          }, timeout ?? 30000);
        });

        try {
          await timeoutPromise;
          console.log(
            "MQTT ----- --->>>>>>>>>>> verification completed successfully"
          );
        } catch (timeoutError) {
          console.error("MQTT verification timed out:", timeoutError);
        }
      } else {
        console.error("MQTT not connected");
        this.state.verifying = false;
        this.removeToast();
        this.showResult("ERROR");
      }
    } catch (error) {
      console.error("Error verifying product:", error);
      this.state.verifying = false;
      this.removeToast();
      this.showResult("ERROR");
      throw error;
    }
  }
  private removeToast(): void {
    if (this.toast) {
      this.toast.remove();
      this.toast = null;
    }
    const existingToasts = document.querySelectorAll(".toast-message-scan");
    existingToasts.forEach((toast) => {
      toast.remove();
    });
  }

  private showResult(status: string, regionExcluded?: boolean): void {
    if (this.state.resultsShown) {
      return;
    }
    this.clearBubbles();
    this.state.resultsShown = true;
    this.removeToast();

    if (this.qrScanner) {
      try {
        console.log("Stopping QR scanner when showing results");
        this.qrScanner.stop();
        this.qrScanner.destroy();
        this.qrScanner = null;
      } catch (err) {
        console.warn("Error while stopping QR scanner:", err);
      }
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        console.log(`Stopping camera track: ${track.kind}`);
        track.stop();
      });
      this.stream = null;
    }

    if (this.videoElement && this.videoElement.srcObject) {
      this.videoElement.srcObject = null;
      this.videoElement.style.display = "none";
    }

    // Reset auto capture state
    this.qrDetectionBuffer = [];
    this.isAutoCaptureInProgress = false;
    if (this.autoCaptureTimeout) {
      window.clearTimeout(this.autoCaptureTimeout);
      this.autoCaptureTimeout = null;
    }

    this.state.scanningQR = false;
    this.state.qrInCenter = false;
    this.state.qrMatched = false;

    this.resetQrCenterStateDebounce();

    this.removeToast();

    const gridContainer = document.getElementById("grid-container");
    if (gridContainer) {
      gridContainer.style.display = "none";
    }

    const cornerOverlay = document.getElementById("corner-overlay");
    if (cornerOverlay) {
      cornerOverlay.style.display = "none";
    }
    let resultConfig = this.getResultConfig(status);
    if (regionExcluded) {
      console.log('--Region excluded--')
      resultConfig = {
        ...resultConfig,
        ...this.getResultConfig('REGION_EXCLUDED_NON_GENUINE'),
      };
    if(status === 'GENUINE') {
      resultConfig = {
        ...resultConfig,
        ...this.getResultConfig('REGION_EXCLUDED_GENUINE')
      };
    }
    }
    this.createResultContainer(resultConfig);
  }

  private getResultConfig(status: string): ResultConfig {
    const ui_result_config =
      this.config?.configs?.experience_config?.ui_result_config || {};
    let resultConfig =
      ui_result_config[status] || DEFAULT_RESULT_CONFIG[status];

    if (!resultConfig) {
      resultConfig =
        ui_result_config["CANNOT_CONFIRM"] ||
        DEFAULT_RESULT_CONFIG["CANNOT_CONFIRM"];
    }

    return resultConfig;
  }

  private createSessionIdDisplay(): void {
    const existingDisplay = document.querySelector('.session-id-display');
    if (existingDisplay) {
      existingDisplay.remove();
    }

    const sessionIdContainer = document.createElement('div');
    sessionIdContainer.classList.add('session-id-display');
    sessionIdContainer.style.position = 'fixed';
    sessionIdContainer.style.bottom = '160px';
    sessionIdContainer.style.left = '50%';
    sessionIdContainer.style.transform = 'translateX(-50%)';
    sessionIdContainer.style.background = 'rgba(36, 36, 36, 0.7)';
    sessionIdContainer.style.color = '#FFFFFF';
    sessionIdContainer.style.padding = '10px 20px';
    sessionIdContainer.style.fontSize = '14px';
    sessionIdContainer.style.borderRadius = '8px';
    sessionIdContainer.style.zIndex = '10000';
    sessionIdContainer.style.width = '95%';
    sessionIdContainer.style.fontFamily = 'Open Sans';
    sessionIdContainer.style.display = 'flex';
    sessionIdContainer.style.alignItems = 'center';
    sessionIdContainer.style.gap = '10px';
    sessionIdContainer.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
    sessionIdContainer.style.pointerEvents = 'none';

    // Create session ID text
    const sessionIdText = document.createElement('span');
    sessionIdText.innerText = `Session ID: ${this.sessionId}`;

    // Create copy button
    const copyButton = document.createElement('button');
    copyButton.innerText = 'Copy';
    copyButton.style.background = '#007bff';
    copyButton.style.color = '#FFFFFF';
    copyButton.style.border = 'none';
    copyButton.style.padding = '6px 12px';
    copyButton.style.borderRadius = '6px';
    copyButton.style.cursor = 'pointer';
    copyButton.style.fontSize = '13px';
    copyButton.style.fontFamily = 'Open Sans';
    copyButton.style.pointerEvents = 'auto';

    copyButton.addEventListener('mouseover', () => {
      copyButton.style.background = '#0056b3';
    });
    copyButton.addEventListener('mouseout', () => {
      copyButton.style.background = '#007bff';
    });

    copyButton.addEventListener('click', () => {
      navigator.clipboard.writeText(this.sessionId).then(() => {
        const originalText = copyButton.innerText;
        
        // Change to checkmark
        copyButton.innerText = '✓';
        
        // Revert back to 'Copy' after 1 second
        setTimeout(() => {
          copyButton.innerText = originalText;
        }, 1000);
      }).catch((err) => {
        console.error('Failed to copy session ID:', err);
      });
    });

    // Append elements to container
    sessionIdContainer.appendChild(sessionIdText);
    sessionIdContainer.appendChild(copyButton);

    // Append to body
    document.body.appendChild(sessionIdContainer);
  }

  private createPreviewBackground(): void {
    const existingBackground = document.getElementById(
      "vyu-preview-background"
    );
    if (existingBackground) {
      existingBackground.remove();
    }

    const backgroundDiv = document.createElement("div");
    backgroundDiv.id = "vyu-preview-background";
    backgroundDiv.style.position = "fixed";
    backgroundDiv.style.top = "0";
    backgroundDiv.style.left = "0";
    backgroundDiv.style.width = "100%";
    backgroundDiv.style.height = "100%";
    backgroundDiv.style.backgroundImage = `url(${this.previewImage})`;
    backgroundDiv.style.backgroundSize = "cover";
    backgroundDiv.style.backgroundPosition = "center";
    backgroundDiv.style.filter = "brightness(0.7)";
    backgroundDiv.style.zIndex = "100";
    if(!this.previewImage) {
      backgroundDiv.style.backgroundColor = "black";
    }
    document.body.appendChild(backgroundDiv);
  }

  private createResultContainer(resultConfig: ResultConfig): void {
    const existingContainer = document.querySelector(".result-container");
    if (existingContainer) {
      return;
    }
    this.removeToast();
    const existingBackground = document.getElementById(
      "vyu-preview-background"
    );
    if (existingBackground) {
      existingBackground.style.zIndex = "100";
    } else {
      this.createPreviewBackground();
    }

    const resultContainer = document.createElement("div");
    resultContainer.className = "result-container";
    resultContainer.style.position = "fixed";
    resultContainer.style.left = "50%";
    resultContainer.style.bottom = "-300px";
    resultContainer.style.transform = "translateX(-50%)";
    resultContainer.style.backgroundColor = resultConfig.bgColour || "white";
    resultContainer.style.borderRadius = "10px";
    resultContainer.style.padding = "20px";
    resultContainer.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.2)";
    resultContainer.style.textAlign = "center";
    resultContainer.style.width = "80%";
    resultContainer.style.maxWidth = "320px";
    resultContainer.style.zIndex = "101";
    resultContainer.style.border = "1px solid";
    resultContainer.style.borderColor = resultConfig.statusColour || "#000";

    const styleElement = document.createElement("style");
    styleElement.innerHTML = `
    @keyframes slideUp {
      from { bottom: -300px; }
      to { bottom: 50%; transform: translateX(-50%) translateY(50%); }
    }
    
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes flipOut {
      0% { transform: rotateX(0deg); }
      100% { transform: rotateX(90deg); opacity: 0; }
    }

    @keyframes flipIn {
      0% { transform: rotateX(-90deg); opacity: 0; }
      100% { transform: rotateX(0deg); opacity: 1; }
    }
  `;
    document.head.appendChild(styleElement);

    resultContainer.style.animation = "slideUp 0.8s ease-out forwards";

    const iconElement = document.createElement("img");
    iconElement.src = resultConfig.icon;
    iconElement.alt = resultConfig.status;
    iconElement.style.width = "50px";
    iconElement.style.height = "50px";
    iconElement.style.marginBottom = "15px";

    const statusElement = document.createElement("div");
    statusElement.textContent = resultConfig.status;
    statusElement.style.margin = "0 0 10px 0";
    statusElement.style.color = resultConfig.statusColour || "#000";
    statusElement.style.fontSize = "24px";
    statusElement.style.fontWeight = "700";

    const textElement = document.createElement("div");
    textElement.style.margin = "0 0 10px 0";
    textElement.style.color = "#333";
    textElement.style.fontSize = "14px";
    textElement.style.fontWeight = "400";
    textElement.style.padding = "8px 20px";
    textElement.style.borderRadius = "8px";
    textElement.style.backgroundColor =
      resultConfig.textBgColour || "rgba(243, 243, 243, 1)";

    const textContent = document.createElement("div");
    textContent.textContent = resultConfig.text;
    textElement.appendChild(textContent);

    let toggleLink: HTMLElement | null = null;
    if (this.serialId) {
      toggleLink = document.createElement("a");
      toggleLink.textContent = "Show Serial ID?";
      toggleLink.style.color = resultConfig.statusColour || "#000";
      toggleLink.style.fontSize = "14px";
      toggleLink.style.fontWeight = "600";
      toggleLink.style.cursor = "pointer";
      toggleLink.style.textDecoration = "underline";
      toggleLink.style.textDecorationThickness = "1px";
      toggleLink.style.display = "block";

      let showingSerial = false;

      toggleLink.addEventListener("click", (e) => {
        e.preventDefault();
        showingSerial = !showingSerial;

        textContent.style.animation = "flipOut 0.3s ease-in-out forwards";

        setTimeout(() => {
          if (showingSerial) {
            textContent.innerHTML = `<div style="color: #333; font-size: 14px; font-weight: 700;">Serial ID: ${this.serialId}</div>`;
            toggleLink.textContent = "Hide Serial ID?";
          } else {
            textContent.textContent = resultConfig.text;
            toggleLink.textContent = "Show Serial ID?";
          }

          textContent.style.animation = "flipIn 0.3s ease-in-out forwards";
        }, 150);
      });
    }

    resultContainer.appendChild(iconElement);
    resultContainer.appendChild(statusElement);
    resultContainer.appendChild(textElement);
    if (toggleLink) {
      resultContainer.appendChild(toggleLink);
    }

    setTimeout(() => {
      const resultRect = resultContainer.getBoundingClientRect();

      buttonContainer.style.top = `${resultRect.bottom + 20}px`;
      buttonContainer.style.opacity = "1";
    }, 1200);

    const buttonContainer = document.createElement("div");
    buttonContainer.style.position = "fixed";
    buttonContainer.style.left = "50%";
    buttonContainer.style.transform = "translateX(-50%)";
    buttonContainer.style.width = "80%";
    buttonContainer.style.maxWidth = "320px";
    buttonContainer.style.display = "flex";
    buttonContainer.style.flexDirection = "column";
    buttonContainer.style.gap = "12px";
    buttonContainer.style.zIndex = "102";
    buttonContainer.style.opacity = "0";
    buttonContainer.style.transition = "opacity 0.5s ease-out";

    if (resultConfig.scanAgainButton) {
      const scanAgainButton = document.createElement("button");
      scanAgainButton.textContent = resultConfig.scanAgainButton;
      scanAgainButton.style.padding = "14px";
      scanAgainButton.style.borderRadius = "8px";
      scanAgainButton.style.backgroundColor = "#ffffff";
      scanAgainButton.style.color = "rgba(68, 68, 68, 1)";
      scanAgainButton.style.border = "none";
      scanAgainButton.style.fontSize = "16px";
      scanAgainButton.style.cursor = "pointer";
      scanAgainButton.style.width = "100%";
      scanAgainButton.style.fontWeight = "600";

      scanAgainButton.style.display = "flex";
      scanAgainButton.style.alignItems = "center";
      scanAgainButton.style.justifyContent = "center";

      if (resultConfig.scanAgainIcon) {
        const iconImg = document.createElement("img");
        iconImg.src = resultConfig.scanAgainIcon;
        iconImg.alt = "scan again icon";
        iconImg.style.width = "16px";
        iconImg.style.height = "16px";
        iconImg.style.marginLeft = "4px";
        iconImg.style.flexShrink = "0";
        scanAgainButton.appendChild(iconImg);
      }
      scanAgainButton.addEventListener("click", this.handleScanAgain);
      buttonContainer.appendChild(scanAgainButton);
    }

    if (resultConfig.button1 && resultConfig.button1Link) {
      const button1 = document.createElement("button");
      button1.textContent = resultConfig.button1;
      button1.style.padding = "14px";
      button1.style.borderRadius = "8px";
      button1.style.backgroundColor = resultConfig.background || "#ffffff";
      button1.style.color = "rgba(68, 68, 68, 1)";
      button1.style.fontSize = "16px";
      button1.style.cursor = "pointer";
      button1.style.width = "100%";
      button1.style.fontWeight = "600";
      button1.style.display = "flex";
      button1.style.alignItems = "center";
      button1.style.justifyContent = "center";

      if (resultConfig.button1Icon) {
        const iconImg = document.createElement("img");
        iconImg.src = resultConfig.button1Icon;
        iconImg.alt = "button1 icon";
        iconImg.style.width = "16px";
        iconImg.style.height = "16px";
        iconImg.style.marginLeft = "4px";
        iconImg.style.flexShrink = "0";
        button1.appendChild(iconImg);
      }
      button1.addEventListener("click", () => {
        if (this.posthogEvent) {
          this.posthogEvent.sendPostHogEvent("button_clicked", {
            button_text: resultConfig.button1,
            redirect_url: resultConfig.button1Link || "",
            result_status_on_screen: resultConfig.status,
            timestamp: new Date().toISOString(),
          });
        }
        if (resultConfig.button1Link) {
          let url = resultConfig.button1Link;
          if (!url.startsWith("http://") && !url.startsWith("https://")) {
            url = "https://" + url;
          }

          window.open(url, "_blank");
        } else if (
          this.toggleCallback &&
          resultConfig.button1 === "Know More"
        ) {
        }
      });

      buttonContainer.appendChild(button1);
    }

    if (resultConfig.button2 && resultConfig.button2Link) {
      const button2 = document.createElement("button");
      button2.textContent = resultConfig.button2;
      button2.style.padding = "14px";
      button2.style.borderRadius = "8px";
      button2.style.backgroundColor = "#ffffff";
      button2.style.color = "rgba(68, 68, 68, 1)";
      button2.style.fontSize = "16px";
      button2.style.cursor = "pointer";
      button2.style.width = "100%";
      button2.style.fontWeight = "600";
      button2.style.display = "flex";
      button2.style.alignItems = "center";
      button2.style.justifyContent = "center";

      if (resultConfig.button2Icon) {
        const iconImg = document.createElement("img");
        iconImg.src = resultConfig.button2Icon;
        iconImg.alt = "button2 icon";
        iconImg.style.width = "16px";
        iconImg.style.height = "16px";
        iconImg.style.marginLeft = "4px";
        iconImg.style.flexShrink = "0";
        button2.appendChild(iconImg);
      }

      button2.addEventListener("click", () => {
        if (this.posthogEvent) {
          this.posthogEvent.sendPostHogEvent("button_clicked", {
            button_text: resultConfig.button2,
            redirect_url: resultConfig.button2Link || "",
            result_status_on_screen: resultConfig.status,
            button_position: "button2",
            timestamp: new Date().toISOString(),
          });
        }
        if (resultConfig.button2Link) {
          let url = resultConfig.button2Link;
          if (!url.startsWith("http://") && !url.startsWith("https://")) {
            url = "https://" + url;
          }
          window.open(url, "_blank");
        }
      });

      buttonContainer.appendChild(button2);
    }

    if (resultConfig.supportButton) {
      const supportButton = document.createElement("button");
      supportButton.textContent = resultConfig.supportButton;
      supportButton.style.padding = "14px";
      supportButton.style.borderRadius = "8px";
      supportButton.style.backgroundColor = "#ffffff";
      supportButton.style.color = "rgba(68, 68, 68, 1)";
      supportButton.style.fontSize = "16px";
      supportButton.style.cursor = "pointer";
      supportButton.style.width = "100%";
      supportButton.style.fontWeight = "600";
      supportButton.style.display = "flex";
      supportButton.style.alignItems = "center";
      supportButton.style.justifyContent = "center";

      if (resultConfig.supportIcon) {
        const iconImg = document.createElement("img");
        iconImg.src = resultConfig.supportIcon;
        iconImg.alt = "support icon";
        iconImg.style.width = "16px";
        iconImg.style.height = "16px";
        iconImg.style.marginLeft = "4px";
        iconImg.style.flexShrink = "0";
        supportButton.appendChild(iconImg);
      }

      supportButton.addEventListener("click", () => {
        if (this.posthogEvent) {
          this.posthogEvent.sendPostHogEvent("button_clicked", {
            button_text: resultConfig.supportButton,
            result_status_on_screen: resultConfig.status,
            timestamp: new Date().toISOString(),
          });
        }

        if (this.showSupportPopupCallback) {
          this.showSupportPopupCallback({
            sessionId: `${this.sessionId}`,
            productInfo: {
              id: this.productId
            },
          });
        }
      });

      buttonContainer.appendChild(supportButton);
    }
    document.body.appendChild(resultContainer);
    document.body.appendChild(buttonContainer);
  }

  public handleScanAgain = (): void => {
    if (this.posthogEvent) {
      this.posthogEvent.sendPostHogEvent("scan_again_clicked", {
        previous_capture_mode: "auto",
        timestamp: new Date().toISOString(),
      });
    }
    console.log(
      "Handling Scan Again click - cleaning up and reusing existing methods"
    );
    this.clearCaptureTimers()
    this.state.resultsShown = false;
    this.state.verifying = false;
    this.state.qrInCenter = false;
    this.state.qrMatched = false;
    this.serialId = "";
    this.imageCountUploaded = 0;
    
    // Reset entity-config related state
    this.entityConfigData = null;
    this.entityConfigPromise = null;

    // Reset auto capture state
    this.qrDetectionBuffer = [];
    this.isAutoCaptureInProgress = false;
    this.lastQrDetectionTime = 0;
    this.isFirstQrDetection = true;
    if (this.autoCaptureTimeout) {
      window.clearTimeout(this.autoCaptureTimeout);
      this.autoCaptureTimeout = null;
    }

    if (this.stableBufferDebounceTimeout) {
      window.clearTimeout(this.stableBufferDebounceTimeout);
      this.stableBufferDebounceTimeout = null;
    }

    if (this.verificationTimeoutId) {
      window.clearTimeout(this.verificationTimeoutId);
      this.verificationTimeoutId = null;
    }

    this.previewImage = "";

    this.clearResultElements();

    this.cleanup(true);

    setTimeout(() => {
      this.startSession().catch((error) => {
        console.error("Session restart failed:", error);
        this.stopCameraAndScanners();
        alert("Something went wrong. Please try after sometime");
      });

      this.startViewfinder().catch((error) => {
        console.error("Error restarting viewfinder:", error);
        const errorToast = document.createElement("div");
        errorToast.textContent =
          "Error restarting scanner. Please refresh the page.";
        errorToast.style.position = "fixed";
        errorToast.style.top = "50%";
        errorToast.style.left = "50%";
        errorToast.style.transform = "translate(-50%, -50%)";
        errorToast.style.background = "#BE2705";
        errorToast.style.color = "#FFFFFF";
        errorToast.style.padding = "10px 20px";
        errorToast.style.borderRadius = "5px";
        errorToast.style.zIndex = "1000";
        document.body.appendChild(errorToast);
      });
    }, 300);
  };

  private clearResultElements(): void {
    console.log("Performing thorough cleanup of all result UI elements");
    this.currentBubbles.forEach((b) => b.element.remove());
    this.currentBubbles = [];
    clearTimeout(this.bubbleRemovalTimer);
    const resultContainer = document.querySelector(".result-container");
    if (resultContainer) resultContainer.remove();
    
    const sessionIdDisplay = document.querySelector('.session-id-display');
    if (sessionIdDisplay) sessionIdDisplay.remove();

    document
      .querySelectorAll(
        "div[style*='position: fixed'][style*='transform: translateX(-50%)']"
      )
      .forEach((elem) => {
        if (elem.querySelector("button")) {
          elem.remove();
        }
      });

    const previewBackground = document.getElementById("vyu-preview-background");
    if (previewBackground) {
      previewBackground.remove();
    }

    document
      .querySelectorAll("div[style*='backgroundImage']")
      .forEach((elem) => {
        elem.remove();
      });

    document
      .querySelectorAll("div[style*='opacity'][style*='transition']")
      .forEach((elem) => {
        elem.remove();
      });

    document.querySelectorAll("button").forEach((button) => {
      if (button.id !== "vyu-capture" && button.className !== "vyu-capture") {
        button.remove();
      }
    });

    document.querySelectorAll("style").forEach((style) => {
      if (
        style.innerHTML &&
        (style.innerHTML.includes("slideUp") ||
          style.innerHTML.includes("fadeIn"))
      ) {
        style.remove();
      }
    });

    document
      .querySelectorAll('[id*="overlay"], [class*="overlay"]')
      .forEach((elem) => {
        if (!elem.classList.contains("toast-message-scan")) {
          elem.remove();
        }
      });

    const gridContainer = document.getElementById("grid-container");
    if (gridContainer) gridContainer.remove();

    const cornerOverlay = document.getElementById("corner-overlay");
    if (cornerOverlay) cornerOverlay.remove();

    this.removeToast();
    this.clearBubbles();
  }

  private updateCanvasSize(): void {
    if (!this.videoElement || !this.canvas) return;

    const offsetWidth = this.videoElement.offsetWidth;
    const offsetHeight = this.videoElement.offsetHeight;

    this.canvas.width = offsetWidth;
    this.canvas.height = offsetHeight;

    this.canvas.style.position = "absolute";
    this.canvas.style.top = `${this.videoElement.offsetTop}px`;
    this.canvas.style.left = `${this.videoElement.offsetLeft}px`;
  }

  private clearOverlay(): void {
    if (this.overlay) {
      while (this.overlay.firstChild) {
        this.overlay.removeChild(this.overlay.firstChild);
      }

      this.overlay.remove();
      this.overlay = null;
    }

    const cornersOverlay = document.getElementById("corner-overlay");
    if (cornersOverlay) cornersOverlay.remove();

    const gridContainer = document.getElementById("grid-container");
    if (gridContainer) gridContainer.remove();

    const toastMessage = document.querySelector(".toast-message-scan");
    if (toastMessage) toastMessage.remove();

    this.centerGridCell = null;
    this.gridCells = [];
  }

  private canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Canvas toBlob returned null"));
            }
          },
          "image/jpeg",
          1.0
        );
      } catch (error) {
        reject(error);
      }
    });
  }


  public cleanup(partial: boolean = false): void {
    console.log("Cleaning up resources...", partial ? "(partial)" : "(full)");

    this.resetQrCenterStateDebounce();
    this.clearBubbles();
    this.cleanupScanRegionHighlights();  
    
    // Clean up auto capture state
    this.qrDetectionBuffer = [];
    this.isAutoCaptureInProgress = false;
    this.clearCaptureTimers()
    if (this.autoCaptureTimeout) {
      window.clearTimeout(this.autoCaptureTimeout);
      this.autoCaptureTimeout = null;
    }

    // Stop camera
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    if (this.qrScanner) {
      try {
        if (typeof this.qrScanner.stop === "function") {
          console.log("Stopping QR scanner...");
          this.qrScanner.stop();
        }
        if (typeof this.qrScanner.destroy === "function") {
          console.log("Destroying QR scanner...");
          this.qrScanner.destroy();
        }
      } catch (err) {
        console.warn("Error stopping QR scanner:", err);
      }
      this.qrScanner = null;
    }

    this.closeAllConnections();

    if (partial) {
      console.log("Partial cleanup completed - connections closed, will reconnect on resume");
      return;
    }

    // Full cleanup - additional cleanup
    this.clearOverlay();

    if (this.timeoutInstance) {
      this.timeoutInstance.clearAll();
    }

    window.removeEventListener("resize", this.updateCanvasSize);
    if (this.videoElement) {
      this.videoElement.removeEventListener(
        "loadeddata",
        this.updateCanvasSize
      );
    }

    console.log("Full cleanup complete.");
  }

  public async run(): Promise<void> {
    try {
      console.log("Running scan flow...");
      // Permissions are handled by the wrapper component

      this.startSession().catch((error) => {
        console.error("Session failed:", error);
        this.stopCameraAndScanners();
        alert("Something went wrong. Please try after sometime");
      });

      await this.startViewfinder();
      console.log("after startviewfinder in Run:");
    } catch (error) {
      console.error("Error running flow:", error);

      if (this.errorCallbackFn) {
        this.errorCallbackFn({
          type: "FLOW_ERROR",
          message: "Error running scan flow",
          error,
        });
      }
    }
  }
}

